import React, { useEffect, useRef, useState, useCallback, useLayoutEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Application, Container, Sprite, Assets } from 'pixi.js';
import { Box, Button, Stack, Typography, Paper, Fade, Snackbar, Alert } from '@mui/material';
import achievementsService from '../services/AchievementsService';
import i18n from '../i18n/config';
import { TOAST_ICONS, formatPoints, formatTeam, SEPARATOR } from '../config/toastIcons';
import Header from './Header';
import GameTable from './GameTable';
import Deck from '../game/Deck';
import DeckRenderer from '../game/DeckRenderer';
import ScoreMarker from '../game/ScoreMarker';
import ScoreMarkerRenderer from '../game/ScoreMarkerRenderer';
import DealManager from '../game/DealManager';
import ToastManager from '../game/ToastManager';
import DealManagerRenderer from '../game/DealManagerRenderer';
import BacigaLogic from '../game/BacigaLogic';
import AIService from '../game/AIService';
import useLandscapeOrientation from '../hooks/useLandscapeOrientation';
import useIsMobileDevice from '../hooks/useIsMobileDevice';
import useGamePause from '../hooks/useGamePause';
import GlobalMatchScore from '../game/GlobalMatchScore';
import useTeamColors from '../hooks/useTeamColors';
import { GAME_TIMINGS } from '../config/gameTimings';
import GameWinnerModal from './GameWinnerModal';
import RoundWinnerModal from './RoundWinnerModal';
import GameStartSequence from './GameStartSequence';
import CardAnimations from '../game/CardAnimations';
import WildcardSelector from './WildcardSelector';
import ScoreHistoryModal from './ScoreHistoryModal';
import GameSettingsInGameModal from './GameSettingsInGameModal/GameSettingsInGameModal';
import ShufflingModal from './ShufflingModal';
import Phase31SummaryModal from './Phase31SummaryModal';
import statsService from '../services/StatisticsService';
import gameLogger from '../services/GameLogger';
import gameStateManager from '../services/GameStateManager';
import soundVibrationService from '../services/SoundVibrationService';
import { debugLog, debugError, debugWarn, debugTable, debugGroup, debugGroupEnd, getDebugDeckBaciga } from '../utils/debugUtils';

const SCORE_COUNT_SPEED = parseInt(process.env.REACT_APP_SCORE_COUNT_SPEED) || 2500;
const COVERED_CARD_TOAST_DELAY = parseInt(process.env.REACT_APP_COVERED_CARD_TOAST_DELAY) || 800;


const Baciga = () => {
    // Refs
    const appRef = useRef(null);
    const deckRef = useRef(null);
    const deckRendererRef = useRef(null);
    const dealManagerRef = useRef(null);
    const dealManagerRendererRef = useRef(null);
    const toastManagerRef = useRef(null);
    const bacigaLogicRef = useRef(new BacigaLogic());
    const headerRef = useRef(null);
    const currentDealerIndexRef = useRef(null); // Ref to track dealer index synchronously
    const handNumberRef = useRef(0); // Ref to track hand number synchronously
    const isEndingHandRef = useRef(false); // Prevent multiple simultaneous endBacigaHand calls
    const isStartingHandRef = useRef(false); // Prevent multiple simultaneous startNewHand calls
    const gameRestoredRef = useRef(false); // Ref to track gameRestored synchronously
    const scoreRendererRef = useRef(null); // Ref to track score marker renderer
    const difficultyRef = useRef(localStorage.getItem('difficulty') || 'NORMAL');
    const aiServiceRef = useRef(null);
    const isMountedRef = useRef(true); // Track if component is mounted
    const roundWonRef = useRef(false); // Track if a round was just won (to prevent endBacigaHand from running)
    const pendingScoreToastTimeoutsRef = useRef({}); // Ref to track pending score toast timeouts for all players
    const prevIsPausedRef = useRef(false); // Track previous isPaused state
    const wasPlayerTeamBehindRef = useRef(false); // Track if player team was ever behind (for comeback achievement)

    // Team colors hook
    const { teamColors } = useTeamColors();

    // Navigation & State
    const location = useLocation();
    const navigate = useNavigate();
    const {
        resumeGame = false,
        savedGameState = null,
        players = [],
        gamePoints = 2,
        gameMode = 'baciga',
        globalScore: incomingGlobalScore,
        startingDealer: initialStartingDealer
    } = location.state || {};

    // History Modal state
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [selectedTeamId, setSelectedTeamId] = useState(null);
    const [selectedTeamHistory, setSelectedTeamHistory] = useState([]);

    // Achievement toast state
    const [achievementToast, setAchievementToast] = useState(null);


    // Register achievement observer and score-change callback for comeback detection
    useEffect(() => {
        const onAchievement = (achievement) => setAchievementToast(achievement);
        achievementsService.registerObserver(onAchievement);

        const onScoreChange = (_teamId, _newScore) => {
            if (team2Marker.score > team1Marker.score) {
                wasPlayerTeamBehindRef.current = true;
            }
        };
        team1Marker.setOnScoreChange(onScoreChange);
        team2Marker.setOnScoreChange(onScoreChange);

        return () => {
            achievementsService.unregisterObserver(onAchievement);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [team1Marker, team2Marker]);

    // Cleanup on unmount to prevent async operations after component is destroyed
    useEffect(() => {
        isMountedRef.current = true;

        return () => {
            debugLog('[Baciga] Component unmounting, cleaning up resources');
            isMountedRef.current = false;

            // Cancel all active card animations to prevent "Cannot read properties of null" errors
            import('../game/CardAnimations').then(module => {
                module.default.cancelAllAnimations();
            });

            // Clear any pending score toast timeouts for all players
            Object.values(pendingScoreToastTimeoutsRef.current).forEach(timeout => {
                if (timeout) clearTimeout(timeout);
            });
            pendingScoreToastTimeoutsRef.current = {};

            // Destroy ToastManager to stop all animations
            if (toastManagerRef.current) {
                toastManagerRef.current.destroy();
                toastManagerRef.current = null;
            }

            // Cleanup DealManagerRenderer
            if (dealManagerRendererRef.current) {
                dealManagerRendererRef.current.clearAllCards();
            }

            // Cleanup DeckRenderer
            if (deckRendererRef.current) {
                deckRendererRef.current.destroy();
                deckRendererRef.current = null;
            }

            // Clear app ref to prevent further access
            appRef.current = null;
        };
    }, []);

    // Global Match Score
    const [globalMatchScore] = useState(() => new GlobalMatchScore(incomingGlobalScore));
    const [globalScore, setGlobalScore] = useState(incomingGlobalScore || { team1: 0, team2: 0 });

    // Score Markers
    const [team1Marker] = useState(() => new ScoreMarker(1, 'oros', 8)); // Use 8 and 9 for markers as per rules
    const [team2Marker] = useState(() => new ScoreMarker(2, 'oros', 9));

    // Game Flow State
    const [startingDealer, setStartingDealer] = useState(initialStartingDealer || null);
    const startingDealerRef = useRef(initialStartingDealer || null);
    // Keep startingDealerRef in sync with startingDealer state
    useEffect(() => {
        startingDealerRef.current = startingDealer;
    }, [startingDealer]);
    const [showGameStart, setShowGameStart] = useState(!resumeGame && !initialStartingDealer);
    const [currentDealerIndex, setCurrentDealerIndex] = useState(null); // Index 0-3
    const [gameReady, setGameReady] = useState(false);
    const [phase, setPhase] = useState('init'); // init, dealing, voting, playing31, roundEnd

    // Game restoration state
    const [isRestoringGame, setIsRestoringGame] = useState(false);
    const [gameRestored, setGameRestored] = useState(false);
    const [currentTurn, setCurrentTurn] = useState(null); // Player index 0-3
    const [handNumber, setHandNumber] = useState(0); // 1 = first hand (must pass if no baciga), 2+ = can call Cards

    // Keep handNumberRef in sync with handNumber state
    useEffect(() => {
        handNumberRef.current = handNumber;
    }, [handNumber]);

    // Voting State (Phase 1)
    const [votes, setVotes] = useState({}); // { playerIndex: 'paso' | 'baciga' | 'bacigote' | 'cartas' }
    const votesRef = useRef({}); // Ref for synchronous access to votes
    const [showVoteButtons, setShowVoteButtons] = useState(false);
    const [declarations, setDeclarations] = useState([]); // For collecting all Báciga/Bacigote declarations
    const declarationsRef = useRef([]); // Ref for synchronous access
    const [waitingForHumanCounter, setWaitingForHumanCounter] = useState(false); // Human can counter-declare
    const [pendingCounterDeclaration, setPendingCounterDeclaration] = useState(null); // 'baciga' | 'bacigote' | null - pending declaration after binding wildcards

    // Card Selection State (for discarding when passing)
    const [isSelectingCards, setIsSelectingCards] = useState(false);
    const [selectedCardIndices, setSelectedCardIndices] = useState([]); // Indices of cards selected for discard

    // Pending Discards - only executed if ALL players pass (not if someone calls Báciga/Bacigote/Cartas)
    const [pendingDiscards, setPendingDiscards] = useState({}); // { playerIndex: [cardIndices] }
    const pendingDiscardsRef = useRef({}); // Ref for synchronous access

    // Player Score State (for conditional buttons)
    const [playerScore, setPlayerScore] = useState(null); // { points, isBaciga, isBacigote }
    const [hasUnboundWildcard, setHasUnboundWildcard] = useState(false); // Track if player has unbound wildcards

    // 31 Phase State
    const [playerHandSums, setPlayerHandSums] = useState({}); // { 0: 15, ... }

    // Covered Cards State - tracks which card is currently covered for each player during Las 31
    // coveredCardIndex: which card is currently covered (null if none)
    // When revealed, coveredCardIndex becomes null, allowing next card to come covered again
    const [cardCoverState, setCardCoverState] = useState({}); // { 0: { coveredCardIndex: null }, ... }
    const cardCoverStateRef = useRef({});

    // Card Hide Selection State - for human player to choose which card to hide at start of Las 31 turn
    const [isSelectingCardToHide, setIsSelectingCardToHide] = useState(false);
    const [pendingTurn31PlayerIdx, setPendingTurn31PlayerIdx] = useState(null); // Player waiting for card hide selection

    // Validated Bácigas State - tracks which players have had their báciga validated and scored
    const [validatedBacigas, setValidatedBacigas] = useState(new Set()); // Set of playerIdx who had their báciga validated
    const validatedBacigasRef = useRef(new Set()); // Ref for synchronous access

    // Header State
    const [headerHeight, setHeaderHeight] = useState(0);
    const isMobile = useIsMobileDevice();
    const { needsRotation, isLandscape, isLocked } = useLandscapeOrientation((landscape) => {
        // Pause game when rotating to portrait mode (only if lock failed)
        if (!landscape && handlePauseToggle && !isLocked) {
            handlePauseToggle(true);
            gameLogger.log('orientation_change_pause', { landscape });
        }
    }, { lockOrientation: true });

    // Winner Modal
    const [showWinnerModal, setShowWinnerModal] = useState(false);
    const [winnerTeamId, setWinnerTeamId] = useState(null);

    // Round Winner Modal (for hand wins, not match wins)
    const [showRoundWinnerModal, setShowRoundWinnerModal] = useState(false);
    const [roundWinnerTeamId, setRoundWinnerTeamId] = useState(null);
    const [pendingNextGame, setPendingNextGame] = useState(null); // Stores transition info for after modal

    // Wildcard Selection
    const [wildcardSelector, setWildcardSelector] = useState({
        open: false,
        playerIdx: null,
        cardIdx: null,
        card: null,
        currentValue: null
    });

    // Torito state - allows one card to be dealt face-up in first round
    const [showToritoButton, setShowToritoButton] = useState(false); // Show button when human is dealer
    const [waitingForToritoDecision, setWaitingForToritoDecision] = useState(false); // Waiting for human to decide

    // In-game settings modal state
    const [inGameSettingsOpen, setInGameSettingsOpen] = useState(false);

    // Shuffling modal state (shown when dealer changes, not on first deal)
    const [showShufflingModal, setShowShufflingModal] = useState(false);
    const [shufflingPlayerName, setShufflingPlayerName] = useState('');
    const [shufflingPlayerAvatar, setShufflingPlayerAvatar] = useState('');

    // Phase 31 Summary Modal state (shown before transitioning to Las 31)
    const [showPhase31SummaryModal, setShowPhase31SummaryModal] = useState(false);
    const [phase31SummaryData, setPhase31SummaryData] = useState(null);

    // Game state getter for pause hook
    const getGameStateForSave = useCallback(() => {
        // Validar que refs existen
        if (!deckRef.current || !dealManagerRef.current) {
            console.warn('[Baciga] Cannot save - refs not ready');
            return null;
        }

        debugLog('💾 [Baciga] Guardando estado del juego:');
        debugLog('  - startingDealer:', startingDealerRef.current);
        debugLog('  - currentDealerIndex:', currentDealerIndex);
        debugLog('  - currentTurn:', currentTurn);
        debugLog('  - phase:', phase);
        debugLog('  - handNumber:', handNumber);
        debugLog('  - team1Score:', team1Marker.score);
        debugLog('  - team2Score:', team2Marker.score);
        debugLog('  - playerCards[0]:', dealManagerRef.current.playerCards[0]?.length || 0, 'cartas');
        debugLog('  - playerCards[1]:', dealManagerRef.current.playerCards[1]?.length || 0, 'cartas');
        debugLog('  - playerCards[2]:', dealManagerRef.current.playerCards[2]?.length || 0, 'cartas');
        debugLog('  - playerCards[3]:', dealManagerRef.current.playerCards[3]?.length || 0, 'cartas');
        debugLog('  - deckCards:', deckRef.current.cards.length, 'cartas');

        return {
            // Metadatos
            gameType: 'baciga',
            savedAt: new Date().toISOString(),

            // Configuración básica (igual que Ronda)
            players: players.map(p => ({ name: p.name, avatar: p.avatar })),
            gamePoints,
            gameMode,
            // Use ref instead of state variable to avoid stale-closure bug where
            // startingDealer reads as null when the callback hasn't been recreated yet.
            startingDealer: startingDealerRef.current ? { name: startingDealerRef.current.name, avatar: startingDealerRef.current.avatar } : null,

            // Scores y estado
            team1Score: team1Marker.score,
            team2Score: team2Marker.score,
            team1Status: team1Marker.isInMalas() ? 'malas' : 'buenas',
            team2Status: team2Marker.isInMalas() ? 'malas' : 'buenas',
            team1History: team1Marker.getHistory(),
            team2History: team2Marker.getHistory(),
            globalScore,

            // Estado del juego
            currentTurn,
            currentDealerIndex,
            handNumber,
            phase,

            // Mazo y cartas
            deckCards: deckRef.current.cards.map(c => ({ suit: c.suit, value: c.value })),
            centerCards: dealManagerRef.current.centerCards.map(c => ({
                suit: c.suit,
                value: c.value,
                ...(c.assignedValue !== undefined && { assignedValue: c.assignedValue })
            })),
            playerCards: {
                0: dealManagerRef.current.playerCards[0]?.map((c, idx) => {
                    const sprite = dealManagerRendererRef.current?.getPlayerCardSprites(0)?.[idx];
                    // Jugador humano: siempre boca arriba (default true si no hay sprite)
                    const isFaceUp = sprite ? (sprite.isFaceUp !== false) : true;
                    return {
                        suit: c.suit,
                        value: c.value,
                        ...(c.assignedValue !== undefined && { assignedValue: c.assignedValue }),
                        isFaceUp
                    };
                }) || [],
                1: dealManagerRef.current.playerCards[1]?.map((c, idx) => {
                    const sprite = dealManagerRendererRef.current?.getPlayerCardSprites(1)?.[idx];
                    return {
                        suit: c.suit,
                        value: c.value,
                        ...(c.assignedValue !== undefined && { assignedValue: c.assignedValue }),
                        ...(sprite && { isFaceUp: sprite.isFaceUp !== false })
                    };
                }) || [],
                2: dealManagerRef.current.playerCards[2]?.map((c, idx) => {
                    const sprite = dealManagerRendererRef.current?.getPlayerCardSprites(2)?.[idx];
                    return {
                        suit: c.suit,
                        value: c.value,
                        ...(c.assignedValue !== undefined && { assignedValue: c.assignedValue }),
                        ...(sprite && { isFaceUp: sprite.isFaceUp !== false })
                    };
                }) || [],
                3: dealManagerRef.current.playerCards[3]?.map((c, idx) => {
                    const sprite = dealManagerRendererRef.current?.getPlayerCardSprites(3)?.[idx];
                    return {
                        suit: c.suit,
                        value: c.value,
                        ...(c.assignedValue !== undefined && { assignedValue: c.assignedValue }),
                        ...(sprite && { isFaceUp: sprite.isFaceUp !== false })
                    };
                }) || []
            },

            // Estados específicos de Baciga (NUEVO)
            votes: votes || {},
            declarations: declarations || [],
            pendingDiscards: pendingDiscards || {},
            playerScore,
            playerHandSums: playerHandSums || {},
            validatedBacigas: Array.from(validatedBacigas || []), // Set -> Array
            showVoteButtons,
            isSelectingCards,
            selectedCardIndices: selectedCardIndices || [],
            waitingForHumanCounter,

            // Estados adicionales si existen
            cardCoverState: cardCoverState || {},
        };
    }, [
        players, gamePoints, gameMode, startingDealer,
        team1Marker, team2Marker, globalScore,
        currentTurn, currentDealerIndex, handNumber, phase,
        votes, declarations, pendingDiscards, playerScore,
        playerHandSums, validatedBacigas, showVoteButtons,
        isSelectingCards, selectedCardIndices, waitingForHumanCounter,
        cardCoverState
    ]);

    // Use pause hook
    const { isPaused, isPausedRef, handlePauseToggle, waitForUnpause } = useGamePause('baciga', getGameStateForSave);

    // Auto-open modal when game is paused (e.g., when switching browser tabs)
    useEffect(() => {
        if (isPaused && !inGameSettingsOpen) {
            setInGameSettingsOpen(true);
        }
    }, [isPaused, inGameSettingsOpen]);

    // Re-render cards when game is resumed (fixes mobile lock/unlock issue)
    useEffect(() => {
        // Only track changes to isPaused, not gameReady
        // If game was paused and is now unpaused, re-render cards
        if (prevIsPausedRef.current && !isPaused && dealManagerRendererRef.current && gameReady) {
            debugLog('[Baciga] Game resumed after pause - re-rendering cards and score markers');
            const currentState = getGameStateForSave();

            // Clear existing cards first to avoid duplicates
            dealManagerRendererRef.current.clearAllCards();

            // Re-render cards
            dealManagerRendererRef.current.renderRestoredCards(currentState)
                .then(async () => {
                    debugLog('[Baciga] Cards successfully re-rendered after resume');

                    // Re-render score markers completely to fix positioning
                    if (scoreRendererRef.current) {
                        // Destroy existing sprites
                        if (scoreRendererRef.current.sprites) {
                            Object.values(scoreRendererRef.current.sprites).forEach(sprite => {
                                if (sprite && sprite.parent) {
                                    sprite.parent.removeChild(sprite);
                                }
                                if (sprite && sprite.destroy) {
                                    sprite.destroy();
                                }
                            });
                            scoreRendererRef.current.sprites = null;
                        }
                        // Re-render from scratch
                        await scoreRendererRef.current.render();
                        await scoreRendererRef.current.update();
                        debugLog('[Baciga] Score markers re-rendered after resume');
                    }
                })
                .catch((error) => {
                    debugError('[Baciga] Error re-rendering cards after resume:', error);
                });
        }

        // Always update the ref to track the current isPaused state
        prevIsPausedRef.current = isPaused;
    }, [isPaused]); // Only depend on isPaused, not gameReady

    // --- Initialization & Setup ---

    // Measure header
    useLayoutEffect(() => {
        const measure = () => {
            if (headerRef.current) {
                const h = Math.round(headerRef.current.getBoundingClientRect().height || 0);
                setHeaderHeight(h);
            }
        };

        measure();

        // Observe header size changes
        let ro;
        if (headerRef.current && typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(() => {
                measure();
            });
            ro.observe(headerRef.current);
        }

        // Initialize AIService
        aiServiceRef.current = new AIService(null, bacigaLogicRef.current);
        aiServiceRef.current.setDifficulty(difficultyRef.current);

        // Difficulty Listener
        const handleDifficultyChange = (e) => {
            debugLog('🎮 [Baciga] Difficulty changed to:', e.detail);
            difficultyRef.current = e.detail;
            if (aiServiceRef.current) aiServiceRef.current.setDifficulty(e.detail);
        };
        window.addEventListener('difficultyChanged', handleDifficultyChange);

        // Also update on window resize as a fallback
        const onResize = () => requestAnimationFrame(measure);
        window.addEventListener('resize', onResize);

        return () => {
            window.removeEventListener('resize', onResize);
            window.removeEventListener('difficultyChanged', handleDifficultyChange);
            if (ro) ro.disconnect();
        };
    }, []);

    // Setup game when GameTable mounts
    const setupGame = (app) => {
        debugLog('🎮 [Baciga] setupGame called with app:', app);
        debugLog('🎮 [Baciga] Screen dimensions:', app.screen.width, 'x', app.screen.height);
        appRef.current = app;

        // Deck
        const deck = new Deck();
        if (getDebugDeckBaciga()) {
            deck.prepareDebugDeckBaciga();
            debugLog(`🐞 [Baciga] DEBUG DECK: "${getDebugDeckBaciga()}"`);
        } else {
            deck.shuffle();
        }
        deckRef.current = deck;
        debugLog('🎮 [Baciga] Deck created with', deck.cards.length, 'cards');

        // Toast Manager
        const toastManager = new ToastManager(app);
        toastManagerRef.current = toastManager;

        // Deal Manager (Logic)
        const dealManager = new DealManager(deck, toastManager);
        dealManagerRef.current = dealManager;

        // Card Sizing (match Ronda behavior on mobile)
        const screenWidth = app.screen.width;
        const screenHeight = app.screen.height;

        let cardWidth, cardHeight;
        if (isMobile) {
            // Same rule as Ronda: scale with screen width on mobile
            cardWidth = Math.floor(screenWidth * 0.06);
            cardHeight = Math.floor(cardWidth * 1.5); // Maintain 2:3 aspect ratio
        } else {
            // Keep existing desktop behavior (slightly smaller cards on small windows)
            const isSmallScreen = screenWidth < 800;
            cardWidth = isSmallScreen ? 70 : 100;
            cardHeight = isSmallScreen ? 105 : 150;
        }

        debugLog('🎮 [Baciga] Card sizing:', {
            screenWidth,
            screenHeight,
            isMobile,
            cardWidth,
            cardHeight,
            percentage: isMobile ? '6%' : (screenWidth < 800 ? 'fixed 70px' : 'fixed 100px')
        });

        // History click handler
        const handleHistoryClick = (teamId) => {
            const marker = teamId === 1 ? team1Marker : team2Marker;
            setSelectedTeamId(teamId);
            setSelectedTeamHistory(marker.getHistory());
            setHistoryModalOpen(true);
        };

        // Score Markers
        const scoreRenderer = new ScoreMarkerRenderer(app, team1Marker, team2Marker);
        scoreRenderer.setCardSize(cardWidth, cardHeight);
        scoreRenderer.setOnHistoryClick(handleHistoryClick);
        scoreRenderer.render();
        scoreRendererRef.current = scoreRenderer;

        // Set up score change callbacks to update the visual markers
        team1Marker.setOnScoreChange(async (teamId, newScore) => {
            debugLog(`📊 [Baciga] Team ${teamId} score changed to ${newScore}`);
            if (scoreRendererRef.current) {
                await scoreRendererRef.current.update();
            }
        });
        team2Marker.setOnScoreChange(async (teamId, newScore) => {
            debugLog(`📊 [Baciga] Team ${teamId} score changed to ${newScore}`);
            if (scoreRendererRef.current) {
                await scoreRendererRef.current.update();
            }
        });

        // Deck Renderer
        const deckRenderer = new DeckRenderer(app, deck);
        deckRenderer.setCardSize(cardWidth, cardHeight);
        deckRendererRef.current = deckRenderer;

        // Deal Manager Renderer
        const dealRenderer = new DealManagerRenderer(app, deckRenderer, dealManager);
        dealRenderer.setCardSize(cardWidth, cardHeight);
        dealRenderer.setPlayerNames(players);
        dealManagerRendererRef.current = dealRenderer;

        // ** Attach Card Click Handler **
        dealRenderer.onCardClick = handleCardClick;

        // Initial Positioning
        if (startingDealer) {
            const dealerIdx = players.findIndex(p => p.id === startingDealer.id);
            debugLog('🎮 [Baciga] Starting dealer:', startingDealer.name, 'index:', dealerIdx);
            setCurrentDealerIndex(dealerIdx);
            currentDealerIndexRef.current = dealerIdx;
            deckRenderer.setPositionByPlayer(dealerIdx, app.screen.width, app.screen.height);
            // Wait for deck to render before starting the hand
            deckRenderer.render().then(() => {
                setGameReady(true);
                // Only start new hand for new games, not for resumed games
                if (!resumeGame) {
                    startNewHand(dealerIdx, 1);
                }
            });
        } else if (resumeGame) {
            // Si estamos restaurando un juego sin dealer, marcar como listo
            // para que se pueda restaurar el estado guardado
            debugLog('🎮 [Baciga] Resuming game without dealer, will restore from saved state...');
            deckRenderer.render().then(() => {
                setGameReady(true);
            });
        } else {
            debugLog('🎮 [Baciga] No starting dealer yet, waiting for GameStartSequence...');
        }
    };

    // --- Game Logic ---

    // Handle Game Start Sequence Completion
    const handleGameStartComplete = async (result) => {
        debugLog('🎮 [Baciga] handleGameStartComplete:', result);
        setStartingDealer(result.startingDealer);
        setShowGameStart(false);

        // Wait a bit for state to settle
        await new Promise(r => setTimeout(r, 500));

        // Check if component is still mounted
        if (!isMountedRef.current) {
            debugLog('[Baciga] Component unmounted during handleGameStartComplete, aborting');
            return;
        }

        if (appRef.current) {
            const dealerIdx = players.findIndex(p => p.id === result.startingDealer.id);
            debugLog('🎮 [Baciga] Setting up game with dealer:', result.startingDealer.name, 'index:', dealerIdx);

            // Update both state and ref
            setCurrentDealerIndex(dealerIdx);
            currentDealerIndexRef.current = dealerIdx;

            if (appRef.current && appRef.current.screen && isMountedRef.current) {
                deckRendererRef.current.setPositionByPlayer(dealerIdx, appRef.current.screen.width, appRef.current.screen.height);
            }

            // IMPORTANT: Await the deck render (it's async)
            if (isMountedRef.current) {
                await deckRendererRef.current.render();
                debugLog('🎮 [Baciga] Deck rendered at dealer position');
                setGameReady(true);
            }

            // Start dealing with the dealer index directly (don't rely on state) - only for new games
            if (!resumeGame && isMountedRef.current) {
                await startNewHand(dealerIdx, 1);
            }
        }
    };

    // Function to restore game state from saved data
    const restoreGameState = useCallback(async (state) => {
        debugLog('[Baciga] Restoring game state:', state);
        setIsRestoringGame(true);

        try {
            // Import Card class
            const Card = (await import('../game/Card')).default;

            // 1. Restaurar Deck
            if (state.deckCards && deckRef.current) {
                deckRef.current.cards = state.deckCards.map(c => new Card(c.suit, c.value));
                debugLog('[Baciga] Deck restored:', deckRef.current.cards.length, 'cards');
            }

            // 2. Restaurar DealManager
            if (state.playerCards && state.centerCards && dealManagerRef.current) {
                dealManagerRef.current.playerCards = {
                    0: state.playerCards[0].map(c => {
                        const card = new Card(c.suit, c.value);
                        if (c.assignedValue !== undefined) card.assignedValue = c.assignedValue;
                        return card;
                    }),
                    1: state.playerCards[1].map(c => {
                        const card = new Card(c.suit, c.value);
                        if (c.assignedValue !== undefined) card.assignedValue = c.assignedValue;
                        return card;
                    }),
                    2: state.playerCards[2].map(c => {
                        const card = new Card(c.suit, c.value);
                        if (c.assignedValue !== undefined) card.assignedValue = c.assignedValue;
                        return card;
                    }),
                    3: state.playerCards[3].map(c => {
                        const card = new Card(c.suit, c.value);
                        if (c.assignedValue !== undefined) card.assignedValue = c.assignedValue;
                        return card;
                    })
                };
                dealManagerRef.current.centerCards = state.centerCards.map(c => {
                    const card = new Card(c.suit, c.value);
                    if (c.assignedValue !== undefined) card.assignedValue = c.assignedValue;
                    return card;
                });
                debugLog('[Baciga] DealManager restored');
            }

            // 3. Restaurar ScoreMarkers
            team1Marker.score = state.team1Score || 0;
            team2Marker.score = state.team2Score || 0;
            if (state.team1History) team1Marker.restoreHistory(state.team1History);
            if (state.team2History) team2Marker.restoreHistory(state.team2History);
            debugLog('[Baciga] ScoreMarkers restored');

            // 4. Restaurar estados de UI
            setCurrentTurn(state.currentTurn);
            setHandNumber(state.handNumber || 1);
            setGlobalScore(state.globalScore || { team1: 0, team2: 0 });
            setPhase(state.phase || 'init');
            debugLog('[Baciga] UI states restored');

            // 5. Restaurar estados específicos de Baciga
            setCurrentDealerIndex(state.currentDealerIndex);
            currentDealerIndexRef.current = state.currentDealerIndex;

            setVotes(state.votes || {});
            votesRef.current = state.votes || {};
            setDeclarations(state.declarations || []);
            declarationsRef.current = state.declarations || [];

            setPendingDiscards(state.pendingDiscards || {});
            pendingDiscardsRef.current = state.pendingDiscards || {};

            setPlayerScore(state.playerScore || null);
            setPlayerHandSums(state.playerHandSums || {});

            // Convertir array a Set para validatedBacigas
            if (state.validatedBacigas) {
                setValidatedBacigas(new Set(state.validatedBacigas));
            }

            setShowVoteButtons(state.showVoteButtons || false);
            setIsSelectingCards(state.isSelectingCards || false);
            setSelectedCardIndices(state.selectedCardIndices || []);
            setWaitingForHumanCounter(state.waitingForHumanCounter || false);

            if (state.cardCoverState) {
                setCardCoverState(state.cardCoverState);
            }

            // 6. Restaurar starting dealer
            if (state.startingDealer) {
                setStartingDealer(state.startingDealer);
            }

            // 7. Renderizar cartas restauradas
            if (dealManagerRendererRef.current) {
                await dealManagerRendererRef.current.renderRestoredCards(state);
                debugLog('[Baciga] Cards rendered from saved state');
            }

            // 8. Renderizar el mazo de cartas restaurado
            if (deckRendererRef.current) {
                // Si hay dealer, posicionar el mazo según el dealer
                if (state.currentDealerIndex !== undefined && state.currentDealerIndex !== null && appRef.current && appRef.current.screen) {
                    deckRendererRef.current.setPositionByPlayer(state.currentDealerIndex, appRef.current.screen.width, appRef.current.screen.height);
                }
                await deckRendererRef.current.render();
                debugLog('[Baciga] Deck renderer updated with', deckRef.current.cards.length, 'cards');
            }

            // 9. Renderizar los ScoreMarkers restaurados
            if (scoreRendererRef.current) {
                await scoreRendererRef.current.update();
                debugLog('[Baciga] ScoreMarkers rendered:', 'Team1:', team1Marker.score, 'Team2:', team2Marker.score);
            }

            debugLog('[Baciga] Game state restored successfully');

            // 10. Continuar el flujo del juego según la fase restaurada
            debugLog('[Baciga] Resuming game flow, phase:', state.phase, 'currentTurn:', state.currentTurn);
            if (state.phase === 'playing31' && state.currentTurn !== null && state.currentTurn !== undefined) {
                // Continuar en la fase de Las 31 con el turno actual
                debugLog('[Baciga] Resuming Las 31 phase at turn:', state.currentTurn);
                // Dar un pequeño delay para que todo se renderice antes de continuar
                await new Promise(r => setTimeout(r, 500));
                processTurn31(state.currentTurn);
            } else if (state.phase === 'voting' && state.currentTurn !== null && state.currentTurn !== undefined) {
                // Continuar en la fase de votación
                debugLog('[Baciga] Resuming voting phase at turn:', state.currentTurn);
                await new Promise(r => setTimeout(r, 500));
                processTurnVoting(state.currentTurn);
            }
        } catch (error) {
            console.error('[Baciga] Error restoring game state:', error);
        } finally {
            setIsRestoringGame(false);
        }
    }, [team1Marker, team2Marker]);

    // Restore game state after setupGame completes (for resumed games)
    useEffect(() => {
        if (gameReady && resumeGame && savedGameState && !gameRestored) {
            debugLog('[Baciga] Game ready, restoring saved state...');
            restoreGameState(savedGameState);
            setGameRestored(true);
            gameRestoredRef.current = true;
        }
    }, [gameReady, resumeGame, savedGameState, gameRestored, restoreGameState]);

    // Expose debug functions to window for console access
    useEffect(() => {
        const updateScoreMarkers = () => {
            if (scoreRendererRef.current) {
                scoreRendererRef.current.update();
            }
        };

        // Only expose debug functions if in debug mode
        const isDebug = process.env.REACT_APP_DEBUG_MODE === 'true';
        if (isDebug) {
            window.addPointsTeam1 = (points) => {
                team1Marker.addPoints(points);
                updateScoreMarkers();
                debugLog(`✅ Added ${points} points to Team 1. New score: ${team1Marker.score}`);
            };

            window.subtractPointsTeam1 = (points) => {
                team1Marker.subtractPoints(points);
                updateScoreMarkers();
                debugLog(`➖ Subtracted ${points} points from Team 1. New score: ${team1Marker.score}`);
            };

            window.addPointsTeam2 = (points) => {
                team2Marker.addPoints(points);
                updateScoreMarkers();
                debugLog(`✅ Added ${points} points to Team 2. New score: ${team2Marker.score}`);
            };

            window.subtractPointsTeam2 = (points) => {
                team2Marker.subtractPoints(points);
                updateScoreMarkers();
                debugLog(`➖ Subtracted ${points} points from Team 2. New score: ${team2Marker.score}`);
            };

            window.getGameState = () => {
                const state = {
                    team1Score: team1Marker.score,
                    team2Score: team2Marker.score,
                    team1Status: team1Marker.isInMalas() ? 'malas' : 'buenas',
                    team2Status: team2Marker.isInMalas() ? 'malas' : 'buenas',
                    globalScore,
                    phase,
                    handNumber,
                    currentTurn: currentTurn !== null ? {
                        index: currentTurn,
                        name: players[currentTurn]?.name
                    } : null,
                    currentDealer: currentDealerIndex !== null ? {
                        index: currentDealerIndex,
                        name: players[currentDealerIndex]?.name
                    } : null
                };
                debugTable(state);
                return state;
            };

            // Log initial state
            debugLog('🎮 Baciga Game Initialized');
            debugLog('📊 Team 1:', team1Marker.score, 'pts', `(${team1Marker.isInMalas() ? 'malas' : 'buenas'})`);
            debugLog('📊 Team 2:', team2Marker.score, 'pts', `(${team2Marker.isInMalas() ? 'malas' : 'buenas'})`);
            debugLog('💡 Debug functions available:');
            debugLog('  - addPointsTeam1(points)');
            debugLog('  - subtractPointsTeam1(points)');
            debugLog('  - addPointsTeam2(points)');
            debugLog('  - subtractPointsTeam2(points)');
            debugLog('  - getGameState()');

            // Cleanup
            return () => {
                delete window.addPointsTeam1;
                delete window.subtractPointsTeam1;
                delete window.addPointsTeam2;
                delete window.subtractPointsTeam2;
                delete window.getGameState;
            };
        }
    }, [team1Marker, team2Marker, globalScore, phase, handNumber, currentTurn, currentDealerIndex, players]);

    const startNewHand = async (dealerIdx, handNum) => {
        debugLog('🎮 [Baciga] startNewHand called, dealerIdx:', dealerIdx, 'handNum:', handNum);

        // Prevent multiple simultaneous calls
        if (isStartingHandRef.current) {
            debugLog('⚠️ [Baciga] startNewHand already in progress, skipping duplicate call');
            return;
        }

        // Don't show torito modal if we're restoring a game
        // Use ref for synchronous access (state may not be updated yet)
        if (isRestoringGame || gameRestoredRef.current) {
            debugLog('⚠️ [Baciga] Game is being restored, skipping torito decision and hand dealing');
            return;
        }

        isStartingHandRef.current = true;

        try {
            // Update the ref to ensure synchronous access
            currentDealerIndexRef.current = dealerIdx;

        // Show shuffling modal for first hand of each round (handNum === 1)
        if (handNum === 1) {
            const dealer = players[dealerIdx];
            setShufflingPlayerName(dealer?.name || `Jugador ${dealerIdx + 1}`);
            setShufflingPlayerAvatar(dealer?.avatar || '');
            setShowShufflingModal(true);

            // Wait for shuffling modal duration
            await new Promise(resolve => setTimeout(resolve, GAME_TIMINGS.dealing.shufflingModal));

            // Hide shuffling modal
            setShowShufflingModal(false);
        }

        setPhase('dealing');
        setHandNumber(handNum);
        setVotes({});
        votesRef.current = {};
        setDeclarations([]); // Clear declarations from previous hand
        declarationsRef.current = [];
        setWaitingForHumanCounter(false);
        setPendingDiscards({}); // Clear any pending discards from previous hand
        pendingDiscardsRef.current = {};
        setValidatedBacigas(new Set());
        validatedBacigasRef.current = new Set();

        // Determine if torito will be used
        let withTorito = false;

        if (dealerIdx === 0) {
            // Human is dealer - show button to let them decide
            debugLog('🎮 [Baciga] Human is dealer, showing torito button');
            setShowToritoButton(true);
            setWaitingForToritoDecision(true);

            // Wait for human decision (button click will call handleToritoDecision)
            await new Promise(resolve => {
                window.resolveToritoDecision = resolve;
            });

            // Check the decision from the promise
            withTorito = window.toritoDecisionResult || false;
            delete window.resolveToritoDecision;
            delete window.toritoDecisionResult;

            setShowToritoButton(false);
            setWaitingForToritoDecision(false);
        } else {
            // AI is dealer - 5% chance of torito
            withTorito = Math.random() < 0.05;
            debugLog(`🎮 [Baciga] AI dealer ${dealerIdx}, torito: ${withTorito}`);

            if (withTorito) {
                // Show toast to inform player that AI is using torito
                const dealerName = players[dealerIdx]?.name || `Jugador ${dealerIdx + 1}`;
                toastManagerRef.current?.showToast(
                    `${dealerName}${SEPARATOR}🎯 TORITO`,
                    2000,
                    dealerIdx
                );
                await new Promise(r => setTimeout(r, 2000)); // Wait for toast
            }
        }

        if (dealManagerRef.current && dealManagerRendererRef.current) {
            // Use Baciga-specific dealing (3 cards per player, no center cards)
            debugLog('🎮 [Baciga] Dealing Baciga hand with torito:', withTorito);
            const dealResult = dealManagerRef.current.dealBacigaHand(dealerIdx, withTorito);
            await dealManagerRendererRef.current.animateDealBacigaHand(dealerIdx, dealResult.toritoInfo);

            // Log all player hands
            for (let i = 0; i < 4; i++) {
                const cards = dealManagerRef.current.getPlayerCards(i);
                const score = bacigaLogicRef.current.calculateScore(cards);
                debugLog(`🃏 [Baciga] Player ${i} hand:`,
                    cards.map(c => `${c.value} ${c.suit}`).join(', '),
                    '| Score:', score.points,
                    '| isBaciga:', score.isBaciga,
                    '| isBacigote:', score.isBacigote);
            }

            startVotingPhase(dealerIdx);
        }
        } finally {
            // Reset flag after hand starts
            isStartingHandRef.current = false;
        }
    };

    const startVotingPhase = async (dealerIdx) => {
        debugLog('🎮 [Baciga] startVotingPhase, dealerIdx:', dealerIdx);
        setPhase('voting');
        setVotes({}); // Clear votes from previous voting round
        votesRef.current = {};

        // Flip torito cards face-down for AI players before voting starts
        if (dealManagerRendererRef.current) {
            await dealManagerRendererRef.current.flipToritoCardsFaceDown();
        }

        const firstVoter = (dealerIdx + 1) % 4;
        debugLog('🎮 [Baciga] First voter:', firstVoter);
        processTurnVoting(firstVoter);
    };

    const processTurnVoting = async (playerIdx) => {
        debugLog('🎮 [Baciga] processTurnVoting, playerIdx:', playerIdx);

        // Set current turn BEFORE pause check so it's accurate if game is saved while paused
        setCurrentTurn(playerIdx);

        // Wait if game is paused
        if (isPausedRef.current) {
            debugLog('⏸️ [Baciga] Game is paused during voting phase, waiting to unpause...');
            await waitForUnpause();
        }

        // Check if someone already called "Cartas" (use votesRef for synchronous access)
        const hasCartasVote = Object.values(votesRef.current).some(v => v === 'cartas');

        if (hasCartasVote) {
            // If someone already called "Cartas", only allow baciga/bacigote votes
            const cards = dealManagerRef.current.getPlayerCards(playerIdx);
            const score = bacigaLogicRef.current.calculateScore(cards);

            if (!score.isBaciga && !score.isBacigote) {
                // Player cannot counter with baciga/bacigote - skip their turn entirely
                debugLog(`🎯 [Baciga] Player ${playerIdx} skipping turn (Cartas already called, no baciga/bacigote)`);

                // Show toast for visibility (only for non-human players)
                if (playerIdx !== 0) {
                    const playerName = players[playerIdx].name;
                    const playerTeamId = (playerIdx === 0 || playerIdx === 2) ? 1 : 2;
                    const playerTeamColor = playerTeamId === 1 ? teamColors.team1.hex : teamColors.team2.hex;
                    safeShowPlayerToast(`${TOAST_ICONS.PASO} Paso`, playerIdx, { type: 'info', teamColor: playerTeamColor, duration: 800 });
                    await new Promise(r => setTimeout(r, 800));
                }

                // Continue to next player WITHOUT storing any vote or discard
                const nextPlayer = (playerIdx + 1) % 4;
                const startPlayer = (currentDealerIndexRef.current + 1) % 4;

                if (nextPlayer === startPlayer) {
                    // Voting complete
                    debugLog('🔄 [Baciga] Voting complete after skipping player');
                    await handleVotingComplete();
                } else {
                    // Continue to next player
                    processTurnVoting(nextPlayer);
                }
                return;
            }
        }

        if (playerIdx === 0) {
            // Calculate player's score for conditional buttons
            updatePlayerScoreAndWildcards();
            setSelectedCardIndices([]); // Reset card selection
            setIsSelectingCards(false);

            // If Cartas was already called, only show baciga/bacigote buttons
            if (hasCartasVote) {
                debugLog('🎮 [Baciga] Cartas already called - human can only counter with baciga/bacigote');
            }

            debugLog('🎮 [Baciga] Showing vote buttons for human player');
            setShowVoteButtons(true);
        } else {
            await new Promise(r => setTimeout(r, 1000));
            // Check again before AI processes vote
            if (isPausedRef.current) {
                debugLog('⏸️ [Baciga] Game paused before AI vote, waiting to unpause...');
                await waitForUnpause();
            }
            // Process AI vote - wildcards are only assigned if needed for báciga/bacigote
            processAiVote(playerIdx);
        }
    };

    /**
     * Assign optimal values to AI's wildcards for báciga/bacigote declaration
     * Only called when AI is going to declare báciga or bacigote
     */
    const assignAiWildcardsForDeclaration = async (playerIdx) => {
        const cards = dealManagerRef.current.getPlayerCards(playerIdx);
        let updated = false;

        cards.forEach((card, idx) => {
            if (bacigaLogicRef.current.isComodin(card) && !bacigaLogicRef.current.isCardBound(card)) {
                const optimal = aiServiceRef.current.assignWildcardForDeclaration(card, cards);
                dealManagerRef.current.updateCardData(playerIdx, idx, { assignedValue: optimal });
                updated = true;
                debugLog(`🤖 [Baciga] AI Player ${playerIdx} assigned value ${optimal} to wildcard ${card.suit} for declaration`);
            }
        });

        return updated;
    };

    /**
     * Assign optimal values to AI's wildcards for Las 31 phase
     * Called when player enters Las 31 and needs to bind wildcards for scoring
     * Takes into account difficulty setting and whether someone already has 31
     * @param {number} playerIdx - AI player index
     * @param {boolean} someoneHas31 - Whether another player already has exactly 31 points
     */
    const assignAiWildcardsForLas31 = async (playerIdx, someoneHas31 = false) => {
        const cards = dealManagerRef.current.getPlayerCards(playerIdx);
        let updated = false;

        cards.forEach((card, idx) => {
            if (bacigaLogicRef.current.isComodin(card) && !bacigaLogicRef.current.isCardBound(card)) {
                const optimal = aiServiceRef.current.assignWildcardForLas31(playerIdx, card, cards, someoneHas31);
                dealManagerRef.current.updateCardData(playerIdx, idx, { assignedValue: optimal });
                updated = true;
                const diff = aiServiceRef.current.getEffectiveDifficulty(playerIdx);
                debugLog(`🤖 [Baciga] AI Player ${playerIdx} assigned value ${optimal} to wildcard ${card.suit} for Las 31 (difficulty: ${diff}, someoneHas31: ${someoneHas31})`);
            }
        });

        // Reposition cards to align wildcards with same-value cards
        if (updated && dealManagerRendererRef.current && (phase === 'playing31' || pendingCounterDeclaration === 'scoreAnnouncement')) {
            await dealManagerRendererRef.current.repositionPlayerCardsFor31(playerIdx);
            debugLog('🎴 [Baciga] AI player cards repositioned after wildcard assignment for player', playerIdx);
        }

        return updated;
    };

    const processAiVote = async (playerIdx) => {
        const cards = dealManagerRef.current.getPlayerCards(playerIdx);
        const score = bacigaLogicRef.current.calculateScore(cards);

        // Delegate vote decision to AIService
        const gameState = {
            handNumber,
            team1Score: team1Marker.score,
            team2Score: team2Marker.score,
            allPassedTwice: handNumber >= 3, // Heuristic: if hand >= 3, enough passing has happened
        };
        const vote = aiServiceRef.current.decideVote(playerIdx, cards, score, gameState);
        debugLog(`🤖 [Baciga] AI Player ${playerIdx} voting:`, vote,
            '| Cards:', cards.map(c => `${c.value} ${c.suit}`).join(', '),
            '| Score:', score.points);

        // Only assign wildcards if AI is declaring báciga or bacigote
        if (vote === 'baciga' || vote === 'bacigote') {
            await assignAiWildcardsForDeclaration(playerIdx);
        }

        // If AI passes, it must discard at least 1 card
        let discardIndices = [];
        if (vote === 'paso') {
            discardIndices = aiServiceRef.current.selectDiscards(playerIdx, cards, score, gameState);
            debugLog(`🤖 [Baciga] AI Player ${playerIdx} discarding indices:`, discardIndices);
        }

        handleVote(playerIdx, vote, discardIndices);
    };


    // Safe toast helpers that check if component is still mounted
    const safeShowPlayerToast = (msg, playerIdx, options) => {
        if (isMountedRef.current && toastManagerRef.current) {
            toastManagerRef.current.showPlayerToast(msg, playerIdx, options);
        }
    };

    const safeShowToast = (msg, options) => {
        if (isMountedRef.current && toastManagerRef.current) {
            toastManagerRef.current.showToast(msg, options);
        }
    };


    const handleVote = async (playerIdx, vote, discardIndices = []) => {
        debugLog(`🗳️ [Baciga] Player ${playerIdx} voted:`, vote, 'discardIndices:', discardIndices);

        // Check if component is still mounted
        if (!isMountedRef.current) {
            debugLog('⚠️ [Baciga] Component unmounted, ignoring vote');
            return;
        }

        // Wait if game is paused
        if (isPausedRef.current) {
            debugLog('⏸️ [Baciga] Game is paused during voting, waiting to unpause...');
            await waitForUnpause();
        }

        // Check again after async wait
        if (!isMountedRef.current) {
            debugLog('⚠️ [Baciga] Component unmounted after unpause, ignoring vote');
            return;
        }

        // Validation for Báciga declaration (Human only)
        if (playerIdx === 0 && (vote === 'baciga' || vote === 'bacigote')) {
            const cards = dealManagerRef.current.getPlayerCards(0);
            const unbound = cards.filter(c => bacigaLogicRef.current.isComodin(c) && !bacigaLogicRef.current.isCardBound(c));
            if (unbound.length > 0) {
                safeShowToast(`${TOAST_ICONS.ERROR} Debes asignar valor a tus comodines antes de cantar`, { type: 'error', duration: 3000 });

                // Open selector for the first unbound one?
                // Or just highlight?
                // Let's just return for now.
                return;
            }
        }

        setShowVoteButtons(false);
        setIsSelectingCards(false);
        setSelectedCardIndices([]);
        setWaitingForHumanCounter(false);

        // Update votes state and keep track of updated votes for synchronous access
        const updatedVotes = { ...votesRef.current, [playerIdx]: vote };
        setVotes(updatedVotes);
        votesRef.current = updatedVotes;

        const playerName = players[playerIdx].name;
        let msg = '';
        let type = 'info';

        if (vote === 'paso') {
            msg = `${TOAST_ICONS.PASO} Paso`;
            // Trigger sound and vibration feedback for passing in Báciga
            soundVibrationService.triggerPasarBaciga();
        }
        else if (vote === 'baciga') { msg = `${TOAST_ICONS.BACIGA} Báciga`; type = 'warning'; }
        else if (vote === 'bacigote') { msg = `${TOAST_ICONS.BACIGOTE} Bacigote`; type = 'error'; }
        else if (vote === 'cartas') { msg = `${TOAST_ICONS.CARD} Cartas`; type = 'warning'; }

        const playerTeamId = (playerIdx === 0 || playerIdx === 2) ? 1 : 2;
        const playerTeamColor = playerTeamId === 1 ? teamColors.team1.hex : teamColors.team2.hex;

        // Show vote toast safely
        safeShowPlayerToast(msg, playerIdx, { type, teamColor: playerTeamColor, duration: 2000 });

        // If player called Báciga/Bacigote, add to declarations
        if (vote === 'baciga' || vote === 'bacigote') {
            const cards = dealManagerRef.current.getPlayerCards(playerIdx);
            const score = bacigaLogicRef.current.calculateScore(cards);

            const newDeclaration = {
                playerIndex: playerIdx,
                type: vote,
                minPoints: score.minPoints,
                cards: cards
            };

            setDeclarations(prev => [...prev, newDeclaration]);
            debugLog('📢 [Baciga] Added declaration:', newDeclaration);
        }

        if (vote !== 'paso' && vote !== 'cartas') {
            // Someone called Báciga/Bacigote - clear all pending discards
            debugLog('🃏 [Baciga] Special vote - clearing pending discards');

            // If human player had selected cards for discard, flip them back to front
            if (selectedCardIndices.length > 0 && playerIdx !== 0) {
                debugLog('🃏 [Baciga] Restoring human player cards after canceling discard');
                await handleCancelSelection();
            }

            setPendingDiscards({});
            pendingDiscardsRef.current = {};

            // Check if human player (0) can counter-declare (hasn't declared yet or voted paso)
            // Use updatedVotes instead of votes state to avoid stale state issues
            if (playerIdx !== 0 && updatedVotes[0] !== 'baciga' && updatedVotes[0] !== 'bacigote' && updatedVotes[0] !== 'paso') {
                const humanCards = dealManagerRef.current.getPlayerCards(0);

                // IMPORTANT: Calculate score as if wildcards were unbound (value = 1)
                // This allows detecting Baciga potential even if player already assigned values
                // Player can change wildcard values when declaring Baciga
                const cardsForCheck = humanCards.map(c => {
                    if (bacigaLogicRef.current.isComodin(c)) {
                        // Temporarily remove assignedValue for Baciga detection
                        const { assignedValue, ...cardWithoutValue } = c;
                        return cardWithoutValue;
                    }
                    return c;
                });

                const humanScore = bacigaLogicRef.current.calculateScore(cardsForCheck);

                debugLog('🎮 [Baciga] Checking if human can counter-declare...');
                debugLog('🎮 [Baciga] Human cards:', humanCards.map(c => `${c.value} ${c.suit}${c.assignedValue ? ` (assigned: ${c.assignedValue})` : ''}`).join(', '));
                debugLog('🎮 [Baciga] Human score (wildcards as 1):', humanScore);

                if (humanScore.isBaciga || humanScore.isBacigote) {
                    // Human can counter-declare!
                    debugLog('🎮 [Baciga] Human player can counter-declare! Score:', humanScore);
                    setPlayerScore(humanScore);
                    setWaitingForHumanCounter(true);
                    setShowVoteButtons(true);
                    return; // Wait for human response
                } else {
                    debugLog('🎮 [Baciga] Human player cannot counter-declare (no baciga/bacigote)');
                }
            }

            // Continue to collect remaining declarations from AI players who haven't voted
            await collectRemainingDeclarations(playerIdx, vote);
        } else if (vote === 'cartas') {
            // Cartas - continue voting to allow baciga/bacigote declarations (which have priority)
            // The vote is already stored, just continue to next player
            debugLog('🎯 [Baciga] Player called Cartas, continuing voting to check for baciga/bacigote');

            // Clear ALL pending discards (when Cartas is called, no one discards)
            debugLog('🎯 [Baciga] Clearing all pending discards (Cartas called)');
            setPendingDiscards({});
            pendingDiscardsRef.current = {};

            const nextPlayer = (playerIdx + 1) % 4;
            const startPlayer = (currentDealerIndexRef.current + 1) % 4;

            if (nextPlayer === startPlayer) {
                // Voting round complete - check priorities
                debugLog('🔄 [Baciga] Voting complete, checking for baciga/bacigote priority over cartas...');
                await handleVotingComplete();
            } else {
                // Continue voting
                processTurnVoting(nextPlayer);
            }
        } else {
            // Player passed - store pending discard
            if (discardIndices.length > 0) {
                debugLog(`🃏 [Baciga] Storing pending discard for player ${playerIdx}:`, discardIndices);
                const newPendingDiscards = { ...pendingDiscardsRef.current, [playerIdx]: discardIndices };
                setPendingDiscards(newPendingDiscards);
                pendingDiscardsRef.current = newPendingDiscards;
            }

            const nextPlayer = (playerIdx + 1) % 4;
            const startPlayer = (currentDealerIndexRef.current + 1) % 4;

            if (nextPlayer === startPlayer) {
                debugLog('🔄 [Baciga] All players passed, executing pending discards...');
                await handleAllPassed();
            } else {
                processTurnVoting(nextPlayer);
            }
        }
    };

    const handleAllPassed = async () => {
        debugLog('🔄 [Baciga] handleAllPassed called, handNumber:', handNumber);
        debugLog('🃏 [Baciga] Pending discards to execute (ref):', pendingDiscardsRef.current);

        // Use the ref for synchronous access to pending discards
        const discardsToExecute = { ...pendingDiscardsRef.current };

        // First, animate all pending discards (visual only)
        for (const [playerIdxStr, cardIndices] of Object.entries(discardsToExecute)) {
            const playerIdx = parseInt(playerIdxStr);
            if (cardIndices && cardIndices.length > 0 && dealManagerRendererRef.current) {
                debugLog(`🃏 [Baciga] Executing visual discard for player ${playerIdx}:`, cardIndices);
                await dealManagerRendererRef.current.animateBacigaDiscard(playerIdx, cardIndices);
            }
        }

        safeShowToast(`${TOAST_ICONS.INFO} Todos pasan${SEPARATOR}Repartiendo cartas`, { type: 'warning', duration: 2000 });
        await new Promise(r => setTimeout(r, 500));

        // Now deal replacement cards (logic + animation)
        // This will: 1) Remove discarded cards from DealManager, 2) Deal new cards, 3) Animate
        const newCards = dealManagerRef.current.dealReplacementCards(
            currentDealerIndexRef.current,
            discardsToExecute
        );

        // Animate the new cards being dealt
        await dealManagerRendererRef.current.animateDealReplacementCards(
            currentDealerIndexRef.current,
            newCards
        );

        // Clear pending discards
        setPendingDiscards({});
        pendingDiscardsRef.current = {};

        // Log new hands for debugging
        for (let i = 0; i < 4; i++) {
            const cards = dealManagerRef.current.getPlayerCards(i);
            const score = bacigaLogicRef.current.calculateScore(cards);
            debugLog(`🃏 [Baciga] Player ${i} new hand:`,
                cards.map(c => `${c.value} ${c.suit}`).join(', '),
                '| Score:', score.points);
        }

        // Continue voting with increased hand number
        setHandNumber(prev => prev + 1);
        await new Promise(r => setTimeout(r, 500));
        startVotingPhase(currentDealerIndexRef.current);
    };

    /**
     * Handle the completion of a voting round
     * Check if there are any baciga/bacigote declarations, which have priority over "cartas"
     */
    const handleVotingComplete = async () => {
        debugLog('🔄 [Baciga] handleVotingComplete called');
        debugLog('🗳️ [Baciga] Final votes:', votesRef.current);

        // Use votesRef for synchronous access
        const currentVotes = votesRef.current;

        // Check if anyone declared baciga or bacigote
        const hasBaciga = Object.values(currentVotes).some(v => v === 'baciga' || v === 'bacigote');
        const hasCartas = Object.values(currentVotes).some(v => v === 'cartas');

        if (hasBaciga) {
            // Baciga/Bacigote has priority - proceed with baciga resolution
            debugLog('🎯 [Baciga] Baciga/Bacigote declared - has priority over cartas');

            // Clear pending discards (not needed when there's baciga)
            setPendingDiscards({});
            pendingDiscardsRef.current = {};

            // Find the first player who declared baciga/bacigote
            const firstDeclarerIdx = parseInt(Object.keys(currentVotes).find(k =>
                currentVotes[k] === 'baciga' || currentVotes[k] === 'bacigote'
            ));

            // Collect all declarations and resolve
            await collectRemainingDeclarations(firstDeclarerIdx, currentVotes[firstDeclarerIdx]);
        } else if (hasCartas) {
            // No baciga/bacigote - proceed with cartas (Las 31)
            debugLog('🎯 [Baciga] Cartas declared and no baciga/bacigote - going to Las 31');

            // Clear pending discards
            setPendingDiscards({});
            pendingDiscardsRef.current = {};

            // Find who declared cartas (just for logging)
            const cartasPlayerIdx = parseInt(Object.keys(currentVotes).find(k => currentVotes[k] === 'cartas'));
            await resolveSpecialVote(cartasPlayerIdx, 'cartas');
        } else {
            // This should not happen - handleVotingComplete is only called when voting completes
            // with at least one non-paso vote or all paso (which goes to handleAllPassed)
            debugLog('⚠️ [Baciga] Unexpected: handleVotingComplete called but no special votes found');
        }
    };

    // --- Card Selection for Discarding ---

    const handleStartCardSelection = () => {
        debugLog('🃏 [Baciga] Starting card selection mode');
        setIsSelectingCards(true);
        setSelectedCardIndices([]);
    };

    const handleToggleCardSelection = async (cardIndex) => {
        debugLog('🃏 [Baciga] Toggle card selection:', cardIndex);
        // Prevent selecting wildcards for discard?
        // Rules in logic say AI never discards them.
        // Let's assume users shouldn't discard them either for now, or just warn.

        const isCurrentlySelected = selectedCardIndices.includes(cardIndex);
        const newSelection = isCurrentlySelected
            ? selectedCardIndices.filter(i => i !== cardIndex)
            : [...selectedCardIndices, cardIndex];

        // Apply visual flip effect to the card sprite
        const playerSprites = dealManagerRendererRef.current?.getPlayerCardSprites(0);
        if (playerSprites && playerSprites[cardIndex]) {
            const sprite = playerSprites[cardIndex];
            const { Assets } = await import('pixi.js');

            if (!isCurrentlySelected) {
                // Flip to back (selecting card for discard)
                const cardBack = localStorage.getItem('cardBack') || '001';
                const backTexturePath = `assets/images/deck/${cardBack}.png`;
                const backTexture = await Assets.load(backTexturePath);

                // Store original texture for later restoration
                sprite.originalTexture = sprite.texture;

                await CardAnimations.flipCard(sprite, backTexture, 0);

                // Update isFaceUp state so revealCards() knows to flip it back in Las 31
                if (sprite && !sprite.destroyed) {
                    sprite.isFaceUp = false;
                }
            } else {
                // Flip back to front (deselecting card)
                // Load the front texture from the card data to ensure correct texture
                if (sprite.cardData && dealManagerRendererRef.current) {
                    const frontTexturePath = dealManagerRendererRef.current.getCardImagePath(
                        sprite.cardData.suit,
                        sprite.cardData.value
                    );
                    const frontTexture = await Assets.load(frontTexturePath);
                    await CardAnimations.flipCard(sprite, frontTexture, 0);

                    // Update isFaceUp state after flipping back to front
                    if (sprite && !sprite.destroyed) {
                        sprite.isFaceUp = true;
                    }
                } else if (sprite.originalTexture) {
                    // Fallback to stored texture if cardData not available
                    await CardAnimations.flipCard(sprite, sprite.originalTexture, 0);

                    // Update isFaceUp state after flipping back to front
                    if (sprite && !sprite.destroyed) {
                        sprite.isFaceUp = true;
                    }
                }
            }
        }

        setSelectedCardIndices(newSelection);
    };

    const handleConfirmPass = () => {
        debugLog('🃏 [Baciga] Confirming pass with selected cards:', selectedCardIndices);
        handleVote(0, 'paso', selectedCardIndices);
    };

    const handleCancelSelection = async () => {
        debugLog('🃏 [Baciga] Cancelling card selection');

        // Flip all selected cards back to front
        const playerSprites = dealManagerRendererRef.current?.getPlayerCardSprites(0);
        if (playerSprites) {
            const { Assets } = await import('pixi.js');
            const flipPromises = selectedCardIndices.map(async (cardIndex) => {
                const sprite = playerSprites[cardIndex];
                if (sprite) {
                    // Load the front texture from the card data to ensure correct texture
                    if (sprite.cardData && dealManagerRendererRef.current) {
                        const frontTexturePath = dealManagerRendererRef.current.getCardImagePath(
                            sprite.cardData.suit,
                            sprite.cardData.value
                        );
                        const frontTexture = await Assets.load(frontTexturePath);
                        await CardAnimations.flipCard(sprite, frontTexture, 0);

                        // Update isFaceUp state after flipping back to front
                        if (sprite && !sprite.destroyed) {
                            sprite.isFaceUp = true;
                        }
                    } else if (sprite.originalTexture) {
                        // Fallback to stored texture if cardData not available
                        await CardAnimations.flipCard(sprite, sprite.originalTexture, 0);

                        // Update isFaceUp state after flipping back to front
                        if (sprite && !sprite.destroyed) {
                            sprite.isFaceUp = true;
                        }
                    }
                }
            });

            await Promise.all(flipPromises);
        }

        setIsSelectingCards(false);
        setSelectedCardIndices([]);
    };

    /**
     * After someone calls Báciga/Bacigote, collect any remaining declarations from other players
     * who haven't voted yet and also have Báciga/Bacigote
     * @param {number} triggerPlayerIdx - Player who triggered the declaration
     * @param {string} triggerVote - Type of vote ('baciga' or 'bacigote')
     * @param {Object} humanDeclaration - Optional explicit human declaration to avoid race conditions
     */
    const collectRemainingDeclarations = async (triggerPlayerIdx, triggerVote, humanDeclaration = null) => {
        debugLog(`📢 [Baciga] Collecting remaining declarations after player ${triggerPlayerIdx} called ${triggerVote}`);

        const manoPlayerIdx = (currentDealerIndexRef.current + 1) % 4;
        const allDeclarations = [];

        // Add the trigger player's declaration
        const triggerCards = dealManagerRef.current.getPlayerCards(triggerPlayerIdx);
        const triggerScore = bacigaLogicRef.current.calculateScore(triggerCards);
        allDeclarations.push({
            playerIndex: triggerPlayerIdx,
            type: triggerVote,
            minPoints: triggerScore.minPoints,
            cards: triggerCards
        });

        // Check all other players (INCLUDING HUMAN) for potential declarations
        for (let i = 0; i < 4; i++) {
            if (i === triggerPlayerIdx) continue;

            const cards = dealManagerRef.current.getPlayerCards(i);
            const score = bacigaLogicRef.current.calculateScore(cards);

            // For human player (0), check if they already voted or have explicit declaration
            // Use votesRef for synchronous access
            const humanVote = votesRef.current[0];
            if (i === 0) {
                if (humanDeclaration) {
                    // Use explicit human declaration passed to avoid race condition
                    allDeclarations.push(humanDeclaration);
                    debugLog(`👤 [Baciga] Human player declared ${humanDeclaration.type} (explicit)!`);
                } else if (humanVote === 'baciga' || humanVote === 'bacigote') {
                    // Human already declared
                    const humanCards = dealManagerRef.current.getPlayerCards(0);
                    const humanScore = bacigaLogicRef.current.calculateScore(humanCards);
                    allDeclarations.push({
                        playerIndex: 0,
                        type: humanVote,
                        minPoints: humanScore.minPoints,
                        cards: humanCards
                    });
                    debugLog(`👤 [Baciga] Human player also declared ${humanVote}!`);
                } else if (humanVote !== 'paso') {
                    // Human hasn't voted yet - don't auto-declare for them
                    debugLog(`👤 [Baciga] Human player hasn't voted yet, skipping auto-declaration`);
                }
                continue;
            }

            // For AI players, auto-declare if they have baciga/bacigote
            if (score.isBacigote) {
                debugLog(`🤖 [Baciga] AI Player ${i} also has Bacigote!`);
                const aiTeamId = (i === 0 || i === 2) ? 1 : 2;
                const aiTeamColor = aiTeamId === 1 ? teamColors.team1.hex : teamColors.team2.hex;
                safeShowPlayerToast(`${TOAST_ICONS.BACIGOTE} Bacigote`, i, { type: 'error', teamColor: aiTeamColor, duration: 2000 });
                await new Promise(r => setTimeout(r, 500));
                allDeclarations.push({
                    playerIndex: i,
                    type: 'bacigote',
                    minPoints: score.minPoints,
                    cards: cards
                });
            } else if (score.isBaciga && triggerVote !== 'bacigote') {
                // Only counter with Báciga if trigger wasn't Bacigote (Bacigote always wins)
                debugLog(`🤖 [Baciga] AI Player ${i} also has Báciga!`);
                const aiTeamId = (i === 0 || i === 2) ? 1 : 2;
                const aiTeamColor = aiTeamId === 1 ? teamColors.team1.hex : teamColors.team2.hex;
                safeShowPlayerToast(`${TOAST_ICONS.BACIGA} Báciga`, i, { type: 'warning', teamColor: aiTeamColor, duration: 2000 });
                await new Promise(r => setTimeout(r, 500));
                allDeclarations.push({
                    playerIndex: i,
                    type: 'baciga',
                    minPoints: score.minPoints,
                    cards: cards
                });
            }
        }

        debugLog('📢 [Baciga] All declarations collected:', allDeclarations.map(d =>
            `P${d.playerIndex}: ${d.type} (${d.minPoints} pts)`
        ).join(', '));

        // Resolve winner
        await resolveDeclarations(allDeclarations, manoPlayerIdx);
    };

    /**
     * Handle human player's counter-declaration
     */
    const handleHumanCounterDeclaration = async (vote) => {
        debugLog(`🎮 [Baciga] Human counter-declaration: ${vote}`);

        // Validation for Báciga/Bacigote declaration - check if wildcards are bound
        if (vote === 'baciga' || vote === 'bacigote') {
            const cards = dealManagerRef.current.getPlayerCards(0);
            const unbound = cards.filter(c => bacigaLogicRef.current.isComodin(c) && !bacigaLogicRef.current.isCardBound(c));

            if (unbound.length > 0) {
                // Check if there are opponents with baciga - if so, must bind wildcards before continuing
                // Use votesRef for synchronous access
                const opponentHasBaciga = Object.keys(votesRef.current).some(idx => {
                    const playerIdx = parseInt(idx);
                    if (playerIdx === 0 || playerIdx === 2) return false; // Skip teammates
                    return votesRef.current[playerIdx] === 'baciga' || votesRef.current[playerIdx] === 'bacigote';
                });

                if (opponentHasBaciga) {
                    // Must bind wildcards immediately
                    safeShowToast(`${TOAST_ICONS.WARNING} Debes amarrar tus comodines antes de cantar${SEPARATOR}Hay contrario con báciga`,
                        { type: 'warning', duration: 4000 });

                    // Set a flag to remember we need to complete this counter-declaration after binding
                    setPendingCounterDeclaration(vote);

                    // Open wildcard selector for first unbound wildcard
                    const unboundIdx = cards.findIndex(c => bacigaLogicRef.current.isComodin(c) && !bacigaLogicRef.current.isCardBound(c));
                    const unboundCard = cards[unboundIdx];
                    setWildcardSelector({
                        open: true,
                        playerIdx: 0,
                        cardIdx: unboundIdx,
                        card: unboundCard,
                        currentValue: unboundCard.assignedValue || null
                    });
                    return; // Wait for wildcard binding
                }

                // No opponent has baciga - can proceed, will bind wildcards later in Las 31 phase
                safeShowToast(`${TOAST_ICONS.INFO} Podrás amarrar tus comodines en Las 31`,
                    { type: 'info', duration: 3000 });
            }
        }

        setWaitingForHumanCounter(false);
        setShowVoteButtons(false);

        if (vote === 'paso') {
            // Human chose not to declare
            const playerTeamColor = teamColors.team1.hex; // Player 0 is always team1
            safeShowPlayerToast('Paso', 0, { type: 'info', teamColor: playerTeamColor, duration: 1500 });
            // Trigger sound and vibration feedback for passing in Báciga
            soundVibrationService.triggerPasarBaciga();
            // Continue with existing declarations
            // Use votesRef for synchronous access
            const currentVotes = votesRef.current;
            const firstDeclarerKey = Object.keys(currentVotes).find(k => currentVotes[k] === 'baciga' || currentVotes[k] === 'bacigote');
            await collectRemainingDeclarations(firstDeclarerKey, currentVotes[firstDeclarerKey]);
        } else {
            // Human declared
            const msg = vote === 'bacigote' ? '¡Bacigote!' : '¡Báciga!';
            const type = vote === 'bacigote' ? 'error' : 'warning';
            const playerTeamColor = teamColors.team1.hex; // Player 0 is always team1
            safeShowPlayerToast(msg, 0, { type, teamColor: playerTeamColor, duration: 2000 });
            const updatedVotes = { ...votesRef.current, 0: vote };
            setVotes(updatedVotes);
            votesRef.current = updatedVotes;

            // Add human's declaration immediately to avoid race condition
            const humanCards = dealManagerRef.current.getPlayerCards(0);
            const humanScore = bacigaLogicRef.current.calculateScore(humanCards);
            const humanDeclaration = {
                playerIndex: 0,
                type: vote,
                minPoints: humanScore.minPoints,
                cards: humanCards
            };

            // Collect all declarations including human's (pass human's declaration explicitly)
            // Use votesRef for synchronous access
            const currentVotesForDeclarer = votesRef.current;
            const firstDeclarerIdx = parseInt(Object.keys(currentVotesForDeclarer).find(k =>
                currentVotesForDeclarer[k] === 'baciga' || currentVotesForDeclarer[k] === 'bacigote'
            ));
            await collectRemainingDeclarations(firstDeclarerIdx, currentVotesForDeclarer[firstDeclarerIdx], humanDeclaration);
        }
    };

    /**
     * Resolve all declarations and determine winner
     */
    const resolveDeclarations = async (allDeclarations, manoPlayerIdx) => {
        debugLog('🏆 [Baciga] Resolving declarations...');

        // Use BacigaLogic to determine winner
        const result = bacigaLogicRef.current.resolveBacigaWinner(allDeclarations, manoPlayerIdx);

        if (!result) {
            debugLog('🏆 [Baciga] No valid declarations');
            setPhase('playing31');
            start31Phase(currentDealerIndexRef.current);
            return;
        }

        debugLog('🏆 [Baciga] Winner:', result);

        if (result.type === 'bacigote') {
            // Bacigote wins immediately
            const winnerName = players[result.winnerIndex].name;
            const winnerTeamId = (result.winnerIndex === 0 || result.winnerIndex === 2) ? 1 : 2;
            const winnerTeamColor = winnerTeamId === 1 ? teamColors.team1.hex : teamColors.team2.hex;
            safeShowPlayerToast(`¡${winnerName} gana con Bacigote!`, result.winnerIndex, { type: 'success', teamColor: winnerTeamColor, duration: 3000 });

            // Reveal winner's cards
            await revealCards(result.winnerIndex);

            // Register bacigote value for statistics (only for player)
            if (result.winnerIndex === 0) {
                const winnerDeclaration = result.allDeclarations.find(d => d.playerIndex === result.winnerIndex);
                const bacigoteValue = winnerDeclaration.minPoints;
                statsService.registerBacigaValue(bacigoteValue, true);
                achievementsService.checkAchievements(statsService.getStats());
            }

            // Award 7 points for Bacigote
            awardPoints(result.winnerIndex, 7, 'Bacigote');

            // Check if partner has pairs/baciga to add
            const partnerIdx = (result.winnerIndex + 2) % 4;
            const partnerCards = dealManagerRef.current.getPlayerCards(partnerIdx);
            const partnerScore = bacigaLogicRef.current.calculateScore(partnerCards);
            const partnerPairs = bacigaLogicRef.current.countPairsAndTrios(partnerCards);

            if (partnerPairs.points > 0) {
                debugLog(`🎮 [Baciga] Partner has ${partnerPairs.pairs} pairs, ${partnerPairs.trios} trios`);
                awardPoints(partnerIdx, partnerPairs.points, 'Ligues del compañero');
            }
            if (partnerScore.isBaciga) {
                debugLog('🎮 [Baciga] Partner also has Báciga!');
                awardPoints(partnerIdx, 2, 'Báciga del compañero');
            }
            if (partnerScore.isBacigote) {
                debugLog('🎮 [Baciga] Partner also has Bacigote!');
                awardPoints(partnerIdx, 7, 'Bacigote del compañero');
            }

            // Reveal all player cards for visual verification (even if covered)
            await dealManagerRendererRef.current.revealAllPlayerCards();

            // Wait a few seconds before proceeding to next hand
            await new Promise(r => setTimeout(r, 3000));

            endBacigaHand();
        } else {
            // Báciga - check if single declaration and would reach 18 points
            const bacigaDeclarations = allDeclarations.filter(d => d.type === 'baciga');

            if (bacigaDeclarations.length === 1) {
                // Only one báciga declared
                const winner = bacigaDeclarations[0];
                const winnerTeamId = (winner.playerIndex === 0 || winner.playerIndex === 2) ? 1 : 2;
                const marker = winnerTeamId === 1 ? team1Marker : team2Marker;
                const currentScore = marker.score;

                // Check if báciga would reach 18 points (game end)
                if (currentScore + 2 >= 18) {
                    debugLog('🏆 [Baciga] Single báciga reaches 18 points - ending game immediately');
                    const winnerName = players[winner.playerIndex].name;
                    const winnerTeamColor = winnerTeamId === 1 ? teamColors.team1.hex : teamColors.team2.hex;

                    safeShowPlayerToast(`${winnerName} se sale con Báciga!`, winner.playerIndex,
                        { type: 'success', teamColor: winnerTeamColor, duration: 3000 });

                    // Reveal winner's cards for verification
                    await revealCards(winner.playerIndex);

                    // Register baciga value for statistics (only for player)
                    if (winner.playerIndex === 0) {
                        statsService.registerBacigaValue(winner.minPoints, false);
                    }

                    // Award 2 points for Báciga
                    awardPoints(winner.playerIndex, 2, 'Báciga');

                    // Reveal all cards for verification
                    await dealManagerRendererRef.current.revealAllPlayerCards();
                    await new Promise(r => setTimeout(r, 3000));

                    endBacigaHand();
                    return;
                }
            }

            // Báciga - go to Las 31 phase
            // For multiple bácigas OR single báciga that doesn't reach 18: validate during 31 phase
            debugLog('🎮 [Baciga] Proceeding to Las 31 phase - báciga will be validated in turn order');

            // Store all declarations for validation during 31 phase (no winner announcement yet)
            setDeclarations(allDeclarations);
            declarationsRef.current = allDeclarations;

            // ** NEW: Check if human player has báciga with unbound wildcards AND there are rival declarations **
            // According to rules: "Si varios rivales cantan báciga, antes de mostrar sus cartas pueden preguntarse 
            // si poseen comodines, y si es así, deben decir qué valor le ponen (amarrar el comodín)"
            const bacigaDeclarationsForBinding = allDeclarations.filter(d => d.type === 'baciga' || d.type === 'bacigote');
            const team1DeclarationsBinding = bacigaDeclarationsForBinding.filter(d => d.playerIndex === 0 || d.playerIndex === 2);
            const team2DeclarationsBinding = bacigaDeclarationsForBinding.filter(d => d.playerIndex === 1 || d.playerIndex === 3);
            const hasRivalDeclarationsBinding = team1DeclarationsBinding.length > 0 && team2DeclarationsBinding.length > 0;
            
            // Check if human player (0) has a declaration and unbound wildcards
            const humanDeclaration = allDeclarations.find(d => d.playerIndex === 0 && (d.type === 'baciga' || d.type === 'bacigote'));
            
            if (hasRivalDeclarationsBinding && humanDeclaration) {
                const humanCards = dealManagerRef.current.getPlayerCards(0);
                const unboundWildcards = humanCards.filter(c => 
                    bacigaLogicRef.current.isComodin(c) && !bacigaLogicRef.current.isCardBound(c)
                );
                
                if (unboundWildcards.length > 0) {
                    debugLog('🃏 [Baciga] Human has báciga with unbound wildcards and there are rival declarations - forcing bind');
                    safeShowToast(`${TOAST_ICONS.WARNING} Debes amarrar tus comodines antes de Las 31${SEPARATOR}Hay báciga rival`, 
                        { type: 'warning', duration: 4000 });
                    
                    // Set pending action to continue to Las 31 after binding
                    setPendingCounterDeclaration('bindBeforeLas31');
                    
                    // Open wildcard selector for first unbound wildcard
                    const unboundIdx = humanCards.findIndex(c => 
                        bacigaLogicRef.current.isComodin(c) && !bacigaLogicRef.current.isCardBound(c)
                    );
                    const unboundCard = humanCards[unboundIdx];
                    setWildcardSelector({
                        open: true,
                        playerIdx: 0,
                        cardIdx: unboundIdx,
                        card: unboundCard,
                        currentValue: unboundCard.assignedValue || null
                    });
                    return; // Wait for wildcard binding before starting Las 31
                }
            }

            await new Promise(r => setTimeout(r, 1500));
            // Show summary modal before transitioning to Las 31
            const summaryData = buildPhase31Summary();
            setPhase31SummaryData(summaryData);
            setShowPhase31SummaryModal(true);
            // Don't transition yet - wait for user to click "Continue"
        }
    };

    /**
     * Build summary data for the Phase 31 Summary Modal
     * Shows what happened during the voting phase before transitioning to Las 31
     */
    const buildPhase31Summary = () => {
        const events = [];
        const currentDeclarations = declarationsRef.current || [];

        // First, add all báciga/bacigote declarations (these are the most important)
        currentDeclarations.forEach(declaration => {
            events.push({
                playerIndex: declaration.playerIndex,
                playerName: players[declaration.playerIndex]?.name || `Jugador ${declaration.playerIndex + 1}`,
                action: declaration.type // 'baciga' or 'bacigote'
            });
        });

        // Then add "cartas" if someone called it (check votes)
        // Use votesRef for synchronous access to final votes
        const finalVotes = votesRef.current;
        for (let i = 0; i < 4; i++) {
            const vote = finalVotes[i];
            if (vote === 'cartas') {
                // Check if this player wasn't already added via declarations
                const alreadyAdded = events.some(e => e.playerIndex === i);
                if (!alreadyAdded) {
                    events.push({
                        playerIndex: i,
                        playerName: players[i]?.name || `Jugador ${i + 1}`,
                        action: 'cartas'
                    });
                }
            }
        }

        // If still no events, check for "paso" votes as fallback
        if (events.length === 0) {
            for (let i = 0; i < 4; i++) {
                const vote = finalVotes[i];
                if (vote === 'paso') {
                    events.push({
                        playerIndex: i,
                        playerName: players[i]?.name || `Jugador ${i + 1}`,
                        action: 'paso'
                    });
                }
            }
        }

        // Sort events by player index for consistent display order
        events.sort((a, b) => a.playerIndex - b.playerIndex);

        return { events };
    };

    /**
     * Handle user clicking "Continue to Las 31" button in the summary modal
     */
    const handlePhase31Continue = () => {
        setShowPhase31SummaryModal(false);
        setPhase('playing31');
        start31Phase(currentDealerIndexRef.current);
    };

    const resolveSpecialVote = async (triggerPlayerIdx, vote) => {
        debugLog(`🎯 [Baciga] resolveSpecialVote, player ${triggerPlayerIdx}, vote: ${vote}`);
        if (vote === 'cartas') {
            // "Cartas" - show summary modal before going to Las 31
            const summaryData = buildPhase31Summary();
            setPhase31SummaryData(summaryData);
            setShowPhase31SummaryModal(true);
            // Don't transition yet - wait for user to click "Continue"
        }
        // Note: baciga/bacigote are now handled by collectRemainingDeclarations
    };

    const start31Phase = async (dealerIdx) => {
        debugLog('🎮 [Baciga] Starting Las 31 phase, dealer:', dealerIdx);
        debugLog(`🎮 [Baciga] Deck size at start of Las 31 (before moving discarded cards): ${dealManagerRef.current.getDeckSize()}`);

        // Initialize card cover state for each player
        const initialState = {};
        for (let i = 0; i < 4; i++) {
            initialState[i] = {
                coveredCardIndex: null  // null = no covered card currently
            };
        }
        setCardCoverState(initialState);
        cardCoverStateRef.current = initialState;

        // First, move discarded cards to bottom of deck (both logically and visually)
        debugLog('🎮 [Baciga] Moving discarded cards to bottom of deck');
        dealManagerRef.current.moveDiscardedCardsToBottomOfDeck();
        debugLog(`🎮 [Baciga] Deck size after moving discarded cards: ${dealManagerRef.current.getDeckSize()}`);

        // Log how many cards each player currently has
        for (let i = 0; i < 4; i++) {
            const cards = dealManagerRef.current.getPlayerCards(i);
            debugLog(`🎮 [Baciga] Player ${i} has ${cards.length} cards at start of Las 31:`, cards.map(c => `${c.value} ${c.suit}`).join(', '));
        }

        await dealManagerRendererRef.current.animateCenterCardsToDeck();

        safeShowToast(`${TOAST_ICONS.JUEGO} Juego al punto${SEPARATOR}Las 31`, { type: 'warning', duration: 2000 });
        await new Promise(r => setTimeout(r, 1000));

        // Don't reveal all cards at once - they will be revealed when each player's turn comes
        const firstPlayer = (dealerIdx + 1) % 4;
        debugLog('🎮 [Baciga] First player in Las 31:', firstPlayer);
        processTurn31(firstPlayer);
    };

    /**
     * Check and validate báciga for player when their turn arrives
     * Player shows their cards and asks if valid
     */
    const checkAndValidateBaciga = async (playerIdx) => {
        const currentDeclarations = declarationsRef.current;
        debugLog(`🔍 [Baciga] checkAndValidateBaciga for player ${playerIdx}, declarations:`, currentDeclarations);
        if (!currentDeclarations || currentDeclarations.length === 0) {
            debugLog(`🔍 [Baciga] No declarations found for validation`);
            return;
        }

        const playerDeclaration = currentDeclarations.find(d => d.playerIndex === playerIdx && d.type === 'baciga');
        if (!playerDeclaration) return;

        // Check if already validated
        if (validatedBacigasRef.current.has(playerIdx)) {
            debugLog(`📊 [Baciga] Player ${playerIdx} báciga already validated`);
            return;
        }

        const playerName = players[playerIdx].name;
        const playerTeamId = (playerIdx === 0 || playerIdx === 2) ? 1 : 2;
        const playerTeamColor = playerTeamId === 1 ? teamColors.team1.hex : teamColors.team2.hex;
        const partnerIdx = (playerIdx + 2) % 4;
        const partnerDeclaration = currentDeclarations.find(d => d.playerIndex === partnerIdx && d.type === 'baciga');

        // Check against rival declarations
        const bacigaDeclarations = currentDeclarations.filter(d => d.type === 'baciga');
        const team1Declarations = bacigaDeclarations.filter(d => d.playerIndex === 0 || d.playerIndex === 2);
        const team2Declarations = bacigaDeclarations.filter(d => d.playerIndex === 1 || d.playerIndex === 3);
        const hasRivalDeclarations = team1Declarations.length > 0 && team2Declarations.length > 0;

        let isValid = false;

        if (!hasRivalDeclarations) {
            // No rival declarations - always valid, award directly without asking
            isValid = true;
            debugLog(`📊 [Baciga] Player ${playerIdx} báciga is valid (no rivals) - awarding directly`);
        } else {
            // Has rival declarations - show question and validate
            safeShowPlayerToast(`${playerName}: ¿Es válida mi báciga?`, playerIdx,
                { type: 'info', teamColor: playerTeamColor, duration: 2000 });
            await new Promise(r => setTimeout(r, 2000));

            const manoPlayerIdx = (currentDealerIndexRef.current + 1) % 4;
            const result = bacigaLogicRef.current.resolveBacigaWinner(bacigaDeclarations, manoPlayerIdx);

            if (result && result.winnerIndex === playerIdx) {
                isValid = true;
                debugLog(`📊 [Baciga] Player ${playerIdx} has the winning báciga`);
                safeShowToast(`${TOAST_ICONS.SUCCESS} Sí, es válida`, { type: 'success', duration: 1500 });
            } else if (partnerDeclaration && result && result.winnerIndex === partnerIdx) {
                // Partner has winning báciga, this player's is also valid
                isValid = true;
                debugLog(`📊 [Baciga] Player ${playerIdx} partner has winning báciga`);
                safeShowToast(`${TOAST_ICONS.SUCCESS} Sí, es válida`, { type: 'success', duration: 1500 });
            } else {
                debugLog(`📊 [Baciga] Player ${playerIdx} báciga is not valid (rival has better)`);
                safeShowToast(`${TOAST_ICONS.ERROR} No, no es válida`, { type: 'error', duration: 1500 });
            }

            await new Promise(r => setTimeout(r, 1500));
        }

        if (isValid) {
            // Determine if this player won or their partner won
            const manoPlayerIdx = (currentDealerIndexRef.current + 1) % 4;
            const result = hasRivalDeclarations ?
                bacigaLogicRef.current.resolveBacigaWinner(bacigaDeclarations, manoPlayerIdx) : null;
            const isWinner = !hasRivalDeclarations || (result && result.winnerIndex === playerIdx);
            const partnerIsWinner = result && result.winnerIndex === partnerIdx;

            // Check if partner also has báciga and should be awarded
            const shouldAwardPartner = partnerDeclaration &&
                (isWinner || partnerIsWinner) &&
                !validatedBacigasRef.current.has(partnerIdx);

            // Mark as validated BEFORE awarding points
            const newValidated = new Set(validatedBacigasRef.current);
            newValidated.add(playerIdx);
            if (shouldAwardPartner) {
                newValidated.add(partnerIdx);
            }
            setValidatedBacigas(newValidated);
            validatedBacigasRef.current = newValidated;

            // If both partners have báciga, award 4 points at once
            if (shouldAwardPartner) {
                const partnerName = players[partnerIdx].name;
                safeShowPlayerToast(`${playerName} y ${partnerName} tienen Báciga (+4)`, playerIdx,
                    { type: 'success', teamColor: playerTeamColor, duration: 2000 });

                // AWARD POINTS IMMEDIATELY AFTER VALIDATION
                await awardPoints(playerIdx, 4, 'Báciga de ambos compañeros');

                // Register statistics for both players if applicable
                if (playerIdx === 0) {
                    statsService.registerBacigaValue(playerDeclaration.minPoints, false);
                }
                if (partnerIdx === 0) {
                    statsService.registerBacigaValue(partnerDeclaration.minPoints, false);
                }
            } else {
                // Award only this player's báciga
                safeShowPlayerToast(`${playerName} - Báciga válida (+2)`, playerIdx,
                    { type: 'success', teamColor: playerTeamColor, duration: 2000 });

                // AWARD POINTS IMMEDIATELY AFTER VALIDATION
                await awardPoints(playerIdx, 2, 'Báciga');

                // Register statistics for player
                if (playerIdx === 0) {
                    statsService.registerBacigaValue(playerDeclaration.minPoints, false);
                }
            }

            await new Promise(r => setTimeout(r, 2000));

            // Check if the team reached 18 points after this báciga validation
            // If so, the game should end immediately via the onWin callback
            // No need to continue with Las 31 phase
            const marker = playerTeamId === 1 ? team1Marker : team2Marker;
            if (marker.score >= 18) {
                debugLog(`🏆 [Baciga] Team ${playerTeamId} reached 18 points with validated báciga - game ending`);
                // The onWin callback will be triggered automatically by ScoreMarker.addPoints
                // Clean up UI state to prevent further actions
                setShowVoteButtons(false);
                setIsSelectingCards(false);
                return; // Exit early to prevent further turn processing
            }

            // Note: Ligues are NOT awarded here during báciga validation
            // They are counted at the end of Las 31 phase during final card count
            // Exception: ligues are revealed early only if they would reach 18 points
        }
    };

    const revealCards = async (playerIdx) => {
        debugLog(`🎮 [Baciga] Revealing cards for player ${playerIdx}`);
        const sprites = dealManagerRendererRef.current.getPlayerCardSprites(playerIdx);
        for (const sprite of sprites) {
            if (!sprite.isFaceUp && sprite && !sprite.destroyed) {
                const texture = await Assets.load(dealManagerRendererRef.current.getCardImagePath(sprite.cardData.suit, sprite.cardData.value));
                // Preserve current rotation when flipping (important for left/right players who have rotated cards)
                const currentRotation = sprite.rotation;
                await CardAnimations.flipCard(sprite, texture, currentRotation, 300);
                if (sprite && !sprite.destroyed) {
                    sprite.isFaceUp = true;
                }
            }
        }

        // ** Reveal bound wildcards for non-human players **
        if (playerIdx !== 0) {
            const cards = dealManagerRef.current.getPlayerCards(playerIdx);
            cards.forEach(card => {
                if (bacigaLogicRef.current.isComodin(card) && bacigaLogicRef.current.isCardBound(card)) {
                    // Get readable value
                    const val = card.assignedValue;
                    let label = String(val);
                    if (val === 1) label = 'As';
                    else if (val === 10) label = 'Sota';
                    else if (val === 11) label = 'Caballo';
                    else if (val === 12) label = 'Rey';

                    const playerTeamId = (playerIdx === 0 || playerIdx === 2) ? 1 : 2;
                    const playerTeamColor = playerTeamId === 1 ? teamColors.team1.hex : teamColors.team2.hex;
                    safeShowPlayerToast(`Comodín = ${label}`, playerIdx, { type: 'info', teamColor: playerTeamColor, duration: 3000 });
                }
            });
        }
    };

    /**
     * Reveal all cards except one (the one to hide) for a player
     * @param {number} playerIdx - Player index
     * @param {number} hideCardIndex - Index of the card to keep hidden (null to reveal all)
     */
    const revealCardsExceptOne = async (playerIdx, hideCardIndex) => {
        debugLog(`🎮 [Baciga] Revealing cards for player ${playerIdx}, hiding card at index: ${hideCardIndex}`);
        const sprites = dealManagerRendererRef.current.getPlayerCardSprites(playerIdx);

        for (let i = 0; i < sprites.length; i++) {
            const sprite = sprites[i];
            if (i === hideCardIndex) {
                // Keep this card hidden (face-down) for AI players
                // For human player (0), card is always visible to them but marked as "covered"
                if (playerIdx !== 0 && sprite && !sprite.destroyed && sprite.isFaceUp) {
                    // Flip to face-down for AI
                    const backTexture = await Assets.load(dealManagerRendererRef.current.getCardBackPath());
                    await CardAnimations.flipCard(sprite, backTexture, sprite.rotation, 300);
                    if (sprite && !sprite.destroyed) {
                        sprite.isFaceUp = false;
                    }
                }
                // Set cover state for this card
                const newState = { coveredCardIndex: hideCardIndex };
                setCardCoverState(prev => ({ ...prev, [playerIdx]: newState }));
                cardCoverStateRef.current[playerIdx] = newState;

                // Mark card visually as covered (for human player shows indicator)
                dealManagerRendererRef.current.markCardAsCovered(playerIdx, hideCardIndex);

                debugLog(`🎮 [Baciga] Player ${playerIdx} hiding card at index ${hideCardIndex}`);
            } else {
                // Reveal this card
                if (!sprite.isFaceUp && sprite && !sprite.destroyed) {
                    const texture = await Assets.load(dealManagerRendererRef.current.getCardImagePath(sprite.cardData.suit, sprite.cardData.value));
                    // Preserve current rotation when flipping (important for left/right players who have rotated cards)
                    const currentRotation = sprite.rotation;
                    await CardAnimations.flipCard(sprite, texture, currentRotation, 300);
                    if (sprite && !sprite.destroyed) {
                        sprite.isFaceUp = true;
                    }
                }
            }
        }

        // Reveal bound wildcards for non-human players (only for visible cards)
        if (playerIdx !== 0) {
            const cards = dealManagerRef.current.getPlayerCards(playerIdx);
            cards.forEach((card, idx) => {
                if (idx !== hideCardIndex && bacigaLogicRef.current.isComodin(card) && bacigaLogicRef.current.isCardBound(card)) {
                    const val = card.assignedValue;
                    let label = String(val);
                    if (val === 1) label = 'As';
                    else if (val === 10) label = 'Sota';
                    else if (val === 11) label = 'Caballo';
                    else if (val === 12) label = 'Rey';

                    const playerTeamId = (playerIdx === 0 || playerIdx === 2) ? 1 : 2;
                    const playerTeamColor = playerTeamId === 1 ? teamColors.team1.hex : teamColors.team2.hex;
                    safeShowPlayerToast(`Comodín = ${label}`, playerIdx, { type: 'info', teamColor: playerTeamColor, duration: 3000 });
                }
            });
        }
    };

    /**
     * AI logic to choose which card to hide at the start of Las 31 turn
     * Delegates to AIService for difficulty-aware decision
     */
    const selectAiCardToHide = (playerIdx) => {
        const cards = dealManagerRef.current.getPlayerCards(playerIdx);
        return aiServiceRef.current.selectCardToHide(playerIdx, cards);
    };

    /**
     * Handle human player selecting a card to hide at start of Las 31 turn
     * @param {number} cardIndex - Index of card to hide, or -1 to reveal all
     */
    const handleSelectCardToHide = async (cardIndex) => {
        debugLog(`🎮 [Baciga] Human player selected card to hide: ${cardIndex}`);
        setIsSelectingCardToHide(false);

        const playerIdx = pendingTurn31PlayerIdx;
        setPendingTurn31PlayerIdx(null);

        if (cardIndex === -1) {
            // Player chose to reveal all cards
            await revealCards(playerIdx);
        } else {
            // Player chose to hide one card
            await revealCardsExceptOne(playerIdx, cardIndex);
        }

        // Continue with the rest of processTurn31
        await continueTurn31AfterCardSelection(playerIdx);
    };

    /**
     * Continue processTurn31 after card selection (for both human and AI)
     */
    const continueTurn31AfterCardSelection = async (playerIdx) => {
        // Move cards to Las 31 position
        await dealManagerRendererRef.current.animatePlayerToLas31(playerIdx);
        await new Promise(r => setTimeout(r, 300));

        // Check if this player has a báciga declaration and validate it
        await checkAndValidateBaciga(playerIdx);

        // Check if game ended due to báciga validation reaching 18 points
        const playerTeamId = (playerIdx === 0 || playerIdx === 2) ? 1 : 2;
        const marker = playerTeamId === 1 ? team1Marker : team2Marker;
        if (marker.score >= 18) {
            debugLog(`🏆 [Baciga] Game ended after báciga validation for player ${playerIdx} - stopping turn processing`);
            return;
        }

        // Check if player can win with current ligues
        const liguesWinCheck = checkLiguesWinCondition(playerIdx);
        if (liguesWinCheck) {
            await handleLiguesWin(playerIdx, liguesWinCheck);
            return;
        }

        if (playerIdx === 0) {
            setShowVoteButtons(true);
        } else {
            await new Promise(r => setTimeout(r, 1000));
            if (isPausedRef.current) {
                debugLog('⏸️ [Baciga] Game paused before AI turn 31, waiting to unpause...');
                await waitForUnpause();
            }
            processAiTurn31(playerIdx);
        }
    };

    /**
     * Handle ligues win scenario (extracted from processTurn31)
     */
    const handleLiguesWin = async (playerIdx, liguesWinCheck) => {
        const playerTeamId = (playerIdx === 0 || playerIdx === 2) ? 1 : 2;
        const playerTeamColor = playerTeamId === 1 ? teamColors.team1.hex : teamColors.team2.hex;

        // Build description of ligues
        let liguesDesc = '';
        if (liguesWinCheck.pairsInfo.trios > 0) {
            liguesDesc += `${liguesWinCheck.pairsInfo.trios} trío(s)`;
        }
        if (liguesWinCheck.pairsInfo.pairs > 0) {
            if (liguesDesc) liguesDesc += ', ';
            liguesDesc += `${liguesWinCheck.pairsInfo.pairs} ligue(s)`;
        }

        // If player has unbound wildcards, they need to bind them first
        if (liguesWinCheck.hasUnboundWildcards) {
            if (playerIdx === 0) {
                safeShowToast(
                    `${TOAST_ICONS.SUCCESS} ¡Puedes ganar amarrando tus comodines!\n${liguesDesc}${SEPARATOR}${liguesWinCheck.liguesPoints} pts`,
                    { type: 'success', duration: 4000 }
                );
                await new Promise(r => setTimeout(r, 2000));
                await bindAllPlayerWildcards(playerIdx);
            } else {
                await assignAiWildcardsForLas31(playerIdx);
            }

            // Reveal any covered cards before showing winning message
            const coverState = cardCoverStateRef.current[playerIdx] || { coveredCardIndex: null };
            if (coverState && coverState.coveredCardIndex !== null) {
                debugLog(`🎮 [Baciga] Player ${playerIdx} revealing covered card before declaring victory`);
                await dealManagerRendererRef.current.revealCoveredCard(playerIdx, coverState.coveredCardIndex);
                const newState = { ...coverState, coveredCardIndex: null };
                setCardCoverState(prev => ({ ...prev, [playerIdx]: newState }));
                cardCoverStateRef.current[playerIdx] = newState;
                await new Promise(r => setTimeout(r, 500));
            }

            safeShowPlayerToast(
                `Con estos ligues (${liguesDesc} = ${liguesWinCheck.liguesPoints} puntos) estamos fuera y ganamos`,
                playerIdx,
                { type: 'success', teamColor: playerTeamColor, duration: 4000 }
            );

            await new Promise(r => setTimeout(r, 4000));
            awardPoints(playerIdx, liguesWinCheck.liguesPoints, 'Ligues (Victoria)');
            return;
        }

        // Player doesn't have unbound wildcards, can win immediately
        const coverState = cardCoverStateRef.current[playerIdx] || { coveredCardIndex: null };
        if (coverState && coverState.coveredCardIndex !== null) {
            debugLog(`🎮 [Baciga] Player ${playerIdx} revealing covered card before declaring victory`);
            await dealManagerRendererRef.current.revealCoveredCard(playerIdx, coverState.coveredCardIndex);
            const newState = { ...coverState, coveredCardIndex: null };
            setCardCoverState(prev => ({ ...prev, [playerIdx]: newState }));
            cardCoverStateRef.current[playerIdx] = newState;
            await new Promise(r => setTimeout(r, 500));
        }

        safeShowPlayerToast(
            `Con los ligues que tengo (${liguesDesc} = ${liguesWinCheck.liguesPoints} puntos), nos salimos y ganamos la báciga`,
            playerIdx,
            { type: 'success', teamColor: playerTeamColor, duration: 4000 }
        );

        await new Promise(r => setTimeout(r, 4000));
        awardPoints(playerIdx, liguesWinCheck.liguesPoints, 'Ligues (Victoria)');
    };

    const processTurn31 = async (playerIdx) => {
        debugLog(`🎮 [Baciga] processTurn31 for player ${playerIdx}`);

        // Set current turn BEFORE pause check so it's accurate if game is saved while paused
        setCurrentTurn(playerIdx);

        // Wait if game is paused
        if (isPausedRef.current) {
            debugLog('⏸️ [Baciga] Game is paused during 31 phase, waiting to unpause...');
            await waitForUnpause();
        }

        // ** NEW: Check if player has báciga declaration with unbound wildcards **
        // If they do AND there are rival declarations, they must bind wildcards before revealing
        if (playerIdx === 0) {
            const currentDeclarations = declarationsRef.current;
            const playerDeclaration = currentDeclarations?.find(d => d.playerIndex === playerIdx && (d.type === 'baciga' || d.type === 'bacigote'));

            if (playerDeclaration) {
                const cards = dealManagerRef.current.getPlayerCards(0);
                const unbound = cards.filter(c => bacigaLogicRef.current.isComodin(c) && !bacigaLogicRef.current.isCardBound(c));

                if (unbound.length > 0) {
                    // Check if there are rival declarations
                    const bacigaDeclarations = currentDeclarations.filter(d => d.type === 'baciga' || d.type === 'bacigote');
                    const team1Declarations = bacigaDeclarations.filter(d => d.playerIndex === 0 || d.playerIndex === 2);
                    const team2Declarations = bacigaDeclarations.filter(d => d.playerIndex === 1 || d.playerIndex === 3);
                    const hasRivalDeclarations = team1Declarations.length > 0 && team2Declarations.length > 0;

                    if (hasRivalDeclarations) {
                        // Must bind wildcards before revealing cards
                        safeShowToast(`${TOAST_ICONS.WARNING} Debes amarrar tus comodines antes de mostrar tus cartas`,
                            { type: 'warning', duration: 4000 });

                        // Set a special flag to continue this turn after binding
                        setPendingCounterDeclaration('continue31Turn');

                        // Open wildcard selector for first unbound wildcard
                        const unboundIdx = cards.findIndex(c => bacigaLogicRef.current.isComodin(c) && !bacigaLogicRef.current.isCardBound(c));
                        const unboundCard = cards[unboundIdx];
                        setWildcardSelector({
                            open: true,
                            playerIdx: 0,
                            cardIdx: unboundIdx,
                            card: unboundCard,
                            currentValue: unboundCard.assignedValue || null
                        });
                        return; // Wait for wildcard binding
                    }
                }
            }
        }

        // Check if this player has a báciga declaration
        const currentDeclarations = declarationsRef.current;
        const playerDeclaration = currentDeclarations?.find(d => d.playerIndex === playerIdx && (d.type === 'baciga' || d.type === 'bacigote'));
        const hasBacigaDeclaration = !!playerDeclaration;

        if (hasBacigaDeclaration) {
            // Player with báciga MUST reveal all cards
            await revealCards(playerIdx);
            await continueTurn31AfterCardSelection(playerIdx);
        } else {
            // Player without báciga can choose to hide ONE card
            if (playerIdx === 0) {
                // Human player: show UI to select which card to hide (or reveal all)
                setPendingTurn31PlayerIdx(playerIdx);
                setIsSelectingCardToHide(true);
                // The turn will continue in handleSelectCardToHide
            } else {
                // AI player: use AI logic to decide which card to hide
                const cardToHideIndex = selectAiCardToHide(playerIdx);
                if (cardToHideIndex !== null) {
                    await revealCardsExceptOne(playerIdx, cardToHideIndex);
                } else {
                    await revealCards(playerIdx);
                }
                await continueTurn31AfterCardSelection(playerIdx);
            }
        }
    };

    const processAiTurn31 = async (playerIdx) => {
        const cards = dealManagerRef.current.getPlayerCards(playerIdx);
        const score = bacigaLogicRef.current.calculateScore(cards);

        if (score.points > 31) {
            handleAction31(playerIdx, 'planto');
            return;
        }

        // If deck is empty and no center cards to recycle, AI must planto
        const deckSize = dealManagerRef.current.getDeckSize();
        const centerSize = dealManagerRef.current.centerCards?.length || 0;
        if (deckSize === 0 && centerSize === 0) {
            debugLog(`🎮 [Baciga] Mazo vacío para jugador ${playerIdx}, forzando planto`);
            handleAction31(playerIdx, 'planto');
            return;
        }

        // Delegate hit/stand decision to AIService
        const action = aiServiceRef.current.decideHitOrStand(playerIdx, cards, score, {});
        handleAction31(playerIdx, action);
    };

    const handleAction31 = async (playerIdx, action) => {
        debugLog(`🎮 [Baciga] handleAction31: player ${playerIdx}, action: ${action}`);

        // Wait if game is paused
        if (isPausedRef.current) {
            debugLog('⏸️ [Baciga] Game is paused during action 31, waiting to unpause...');
            await waitForUnpause();
        }

        setShowVoteButtons(false);

        if (action === 'planto') {
            const playerTeamId = (playerIdx === 0 || playerIdx === 2) ? 1 : 2;
            const playerTeamColor = playerTeamId === 1 ? teamColors.team1.hex : teamColors.team2.hex;
            safeShowPlayerToast('Me planto', playerIdx, { type: 'info', teamColor: playerTeamColor, duration: 1500 });

            // Hide AI player's cards after they finish their turn (before passing to next player)
            // Human player (0) keeps their cards visible
            if (playerIdx !== 0) {
                await new Promise(r => setTimeout(r, 500)); // Small delay before hiding
                await dealManagerRendererRef.current.hidePlayerCards(playerIdx);
            }

            if (playerIdx === currentDealerIndexRef.current) {
                end31Phase();
            } else {
                processTurn31((playerIdx + 1) % 4);
            }

        } else if (action === 'carta') {
            // Check if deck is empty and recycle discarded cards if needed
            const deckSize = dealManagerRef.current.getDeckSize();
            debugLog(`🎮 [Baciga] handleAction31 carta - Deck size before dealing: ${deckSize}`);
            if (deckSize === 0) {
                debugLog('🎮 [Baciga] Deck is empty, recycling discarded cards to bottom of deck');
                debugLog('⚠️ [Baciga] WARNING: Deck is empty during Las 31 phase! This should not happen if discarded cards were moved correctly at phase start.');
                dealManagerRef.current.moveDiscardedCardsToBottomOfDeck();
                await dealManagerRendererRef.current.animateCenterCardsToDeck();
            }

            // Deal a new card to the player
            const card = dealManagerRef.current.dealCardToPlayer(playerIdx);
            debugLog(`🎮 [Baciga] Dealt card to player ${playerIdx}:`, card?.suit, card?.value);

            if (card) {
                const state = cardCoverStateRef.current[playerIdx] || { coveredCardIndex: null };
                const currentCards = dealManagerRef.current.getPlayerCards(playerIdx);
                const newCardIndex = currentCards.length - 1;

                // Determine if this card should come "covered" (hidden from other players)
                // Rule: Card comes COVERED if player does NOT currently have a covered card
                // If player already has a covered card, new cards come UNCOVERED
                // When player reveals a covered card, next card comes COVERED again
                // Important: The player WHO RECEIVES the card ALWAYS sees it, it's only hidden from OTHERS
                const hasCurrentlyCoveredCard = state.coveredCardIndex !== null;
                const shouldComeCovered = !hasCurrentlyCoveredCard;

                debugLog(`🎮 [Baciga] Player ${playerIdx} - hasCurrentlyCoveredCard: ${hasCurrentlyCoveredCard}, shouldComeCovered: ${shouldComeCovered}`);

                // For player 0 (human): card is always face-up (they see their own card)
                // For players 1-3 (AI): card is face-down if covered (human shouldn't see it yet)
                const shouldRenderFaceDown = shouldComeCovered && playerIdx !== 0;

                // Deal the card
                await dealManagerRendererRef.current.animateDealCardFor31(playerIdx, shouldRenderFaceDown);

                // Update state and mark card as covered if needed
                if (shouldComeCovered) {
                    const newState = {
                        ...state,
                        coveredCardIndex: newCardIndex
                    };
                    setCardCoverState(prev => ({ ...prev, [playerIdx]: newState }));
                    cardCoverStateRef.current[playerIdx] = newState;

                    // Mark the card with visual indicator (for human player, shows which card is covered)
                    dealManagerRendererRef.current.markCardAsCovered(playerIdx, newCardIndex);
                }

                const cards = dealManagerRef.current.getPlayerCards(playerIdx);
                const score = bacigaLogicRef.current.calculateScore(cards);
                debugLog(`🎮 [Baciga] Player ${playerIdx} score after new card:`, score.points,
                    'isCosas:', score.isCosas);

                // ** NEW: Check if player can win with ligues after receiving a new card **
                const liguesWinCheck = checkLiguesWinCondition(playerIdx);
                if (liguesWinCheck) {
                    const playerTeamId = (playerIdx === 0 || playerIdx === 2) ? 1 : 2;
                    const playerTeamColor = playerTeamId === 1 ? teamColors.team1.hex : teamColors.team2.hex;

                    // Build description of ligues
                    let liguesDesc = '';
                    if (liguesWinCheck.pairsInfo.trios > 0) {
                        liguesDesc += `${liguesWinCheck.pairsInfo.trios} trío(s)`;
                    }
                    if (liguesWinCheck.pairsInfo.pairs > 0) {
                        if (liguesDesc) liguesDesc += ', ';
                        liguesDesc += `${liguesWinCheck.pairsInfo.pairs} ligue(s)`;
                    }

                    // If player has unbound wildcards, they need to bind them first
                    if (liguesWinCheck.hasUnboundWildcards) {
                        if (playerIdx === 0) {
                            // Human player needs to bind wildcards to secure the win
                            safeShowToast(
                                `${TOAST_ICONS.SUCCESS} ¡Puedes ganar amarrando tus comodines!\n${liguesDesc}${SEPARATOR}${liguesWinCheck.liguesPoints} pts`,
                                { type: 'success', duration: 4000 }
                            );
                            await new Promise(r => setTimeout(r, 2000));

                            // Bind all wildcards to secure the win
                            await bindAllPlayerWildcards(playerIdx);
                        } else {
                            // AI player - bind wildcards automatically for ligues
                            await assignAiWildcardsForLas31(playerIdx);
                        }

                        // Reveal any covered cards before showing winning message so everyone can verify
                        const coverState = cardCoverStateRef.current[playerIdx] || { coveredCardIndex: null };
                        if (coverState && coverState.coveredCardIndex !== null) {
                            debugLog(`🎮 [Baciga] Player ${playerIdx} revealing covered card before declaring victory`);
                            await dealManagerRendererRef.current.revealCoveredCard(playerIdx, coverState.coveredCardIndex);
                            const newState = { ...coverState, coveredCardIndex: null };
                            setCardCoverState(prev => ({ ...prev, [playerIdx]: newState }));
                            cardCoverStateRef.current[playerIdx] = newState;
                            await new Promise(r => setTimeout(r, 500));
                        }

                        // After binding, show winning toast
                        safeShowPlayerToast(
                            `Con estos ligues (${liguesDesc} = ${liguesWinCheck.liguesPoints} puntos) estamos fuera y ganamos`,
                            playerIdx,
                            { type: 'success', teamColor: playerTeamColor, duration: 4000 }
                        );

                        await new Promise(r => setTimeout(r, 4000));

                        // Award the ligues points (triggers win via marker.addPoints)
                        awardPoints(playerIdx, liguesWinCheck.liguesPoints, 'Ligues (Victoria)');

                        // Win callback will be triggered automatically, no need to continue
                        return;
                    }

                    // Player doesn't have unbound wildcards, can win immediately

                    // Reveal any covered cards before showing winning message so everyone can verify
                    const coverState = cardCoverStateRef.current[playerIdx] || { coveredCardIndex: null };
                    if (coverState && coverState.coveredCardIndex !== null) {
                        debugLog(`🎮 [Baciga] Player ${playerIdx} revealing covered card before declaring victory`);
                        await dealManagerRendererRef.current.revealCoveredCard(playerIdx, coverState.coveredCardIndex);
                        const newState = { ...coverState, coveredCardIndex: null };
                        setCardCoverState(prev => ({ ...prev, [playerIdx]: newState }));
                        cardCoverStateRef.current[playerIdx] = newState;
                        await new Promise(r => setTimeout(r, 500));
                    }

                    // Show winning toast
                    safeShowPlayerToast(
                        `Con estos ligues (${liguesDesc} = ${liguesWinCheck.liguesPoints} puntos) estamos fuera y ganamos`,
                        playerIdx,
                        { type: 'success', teamColor: playerTeamColor, duration: 4000 }
                    );

                    await new Promise(r => setTimeout(r, 4000));

                    // Award the ligues points (triggers win via marker.addPoints)
                    awardPoints(playerIdx, liguesWinCheck.liguesPoints, 'Ligues (Victoria)');

                    // Win callback will be triggered automatically, no need to continue
                    return;
                }

                // Show score toast for all players after receiving a requested card
                const playerCoverState = cardCoverStateRef.current[playerIdx] || { coveredCardIndex: null };
                const coveredCardIndex = playerCoverState.coveredCardIndex;

                // Check for unbound comodines (wildcards without assigned value)
                // IMPORTANT: Don't count covered wildcards here - they should be treated as "la tapada" only
                const unboundComodines = cards.filter((card, idx) => {
                    const isCovered = idx === coveredCardIndex;
                    const isUnboundWildcard = bacigaLogicRef.current.isComodin(card) && !bacigaLogicRef.current.isCardBound(card);
                    // Only count unbound wildcards that are NOT covered (to avoid revealing wildcard info)
                    return isUnboundWildcard && !isCovered;
                });

                // Calculate sum excluding covered card and unbound comodines
                let visibleSum = 0;
                cards.forEach((card, idx) => {
                    const isCovered = idx === coveredCardIndex;
                    const isUnboundComodin = bacigaLogicRef.current.isComodin(card) &&
                                            !bacigaLogicRef.current.isCardBound(card);

                    if (!isCovered && !isUnboundComodin) {
                        visibleSum += bacigaLogicRef.current.getCardValue(card);
                    }
                });

                let scoreMessage = '';
                const hasCoveredCard = coveredCardIndex !== null;
                const hasUnboundComodines = unboundComodines.length > 0;

                if (hasCoveredCard && hasUnboundComodines) {
                    // Has both covered card and unbound wildcard(s) that are REVEALED
                    // This means: covered card + visible unbound wildcard(s)
                    scoreMessage = `${visibleSum}, la tapada y ${unboundComodines.length === 1 ? 'el comodín' : 'los comodines'}`;
                } else if (hasCoveredCard) {
                    // Has only covered card (could be a wildcard, but we don't reveal that info)
                    scoreMessage = `${visibleSum} y la tapada`;
                } else if (hasUnboundComodines) {
                    // Has only unbound wildcard(s) - all visible
                    scoreMessage = `${visibleSum} y ${unboundComodines.length === 1 ? 'el comodín' : 'los comodines'}`;
                } else {
                    // All cards are revealed and no unbound wildcards
                    scoreMessage = `${visibleSum}`;
                }

                const playerTeamId = (playerIdx === 0 || playerIdx === 2) ? 1 : 2;
                const playerTeamColor = playerTeamId === 1 ? teamColors.team1.hex : teamColors.team2.hex;

                // When there's a covered card, delay the toast to allow revealing
                // This prevents showing "y la tapada" when the card is being revealed immediately
                if (hasCoveredCard) {
                    // Clear any existing pending toast for this player
                    if (pendingScoreToastTimeoutsRef.current[playerIdx]) {
                        clearTimeout(pendingScoreToastTimeoutsRef.current[playerIdx]);
                        pendingScoreToastTimeoutsRef.current[playerIdx] = null;
                    }

                    // Delay showing the toast with "tapada" to give time for reveal animation
                    pendingScoreToastTimeoutsRef.current[playerIdx] = setTimeout(() => {
                        safeShowPlayerToast(scoreMessage, playerIdx, {
                            type: 'info',
                            teamColor: playerTeamColor,
                            duration: 2000
                        });
                        pendingScoreToastTimeoutsRef.current[playerIdx] = null;
                    }, COVERED_CARD_TOAST_DELAY);
                } else {
                    // When no covered card, show toast immediately
                    safeShowPlayerToast(scoreMessage, playerIdx, {
                        type: 'info',
                        teamColor: playerTeamColor,
                        duration: 2000
                    });
                }

                if (score.isCosas) {
                    // Cancel any pending score toast - "LAS COSAS" is what matters
                    if (pendingScoreToastTimeoutsRef.current[playerIdx]) {
                        clearTimeout(pendingScoreToastTimeoutsRef.current[playerIdx]);
                        pendingScoreToastTimeoutsRef.current[playerIdx] = null;
                    }

                    const playerTeamId = (playerIdx === 0 || playerIdx === 2) ? 1 : 2;
                    const playerTeamColor = playerTeamId === 1 ? teamColors.team1.hex : teamColors.team2.hex;

                    // Wait a moment for the card dealing animation to complete
                    await new Promise(r => setTimeout(r, GAME_TIMINGS.lasCosas.afterCardDeal));

                    // 1. First, reveal any covered card so the player can see what they got
                    const coverState = cardCoverStateRef.current[playerIdx] || { coveredCardIndex: null };
                    if (coverState && coverState.coveredCardIndex !== null) {
                        debugLog(`🎴 [Baciga] Revealing covered card before Las Cosas for player ${playerIdx}`);
                        await dealManagerRendererRef.current.revealCoveredCard(playerIdx, coverState.coveredCardIndex);
                        const newState = { ...coverState, coveredCardIndex: null };
                        setCardCoverState(prev => ({ ...prev, [playerIdx]: newState }));
                        cardCoverStateRef.current[playerIdx] = newState;
                        // Give time to see the revealed card
                        await new Promise(r => setTimeout(r, GAME_TIMINGS.lasCosas.afterRevealCard));
                    }

                    // 2. Check if there are unbound wildcards that need to be bound for "Las Cosas"
                    const currentCards = dealManagerRef.current.getPlayerCards(playerIdx);
                    const unboundWildcards = currentCards.filter(c =>
                        bacigaLogicRef.current.isComodin(c) && !bacigaLogicRef.current.isCardBound(c)
                    );

                    if (unboundWildcards.length > 0) {
                        debugLog(`🎴 [Baciga] Binding ${unboundWildcards.length} wildcards for Las Cosas`);
                        // For AI players, bind wildcards automatically
                        if (playerIdx !== 0) {
                            await assignAiWildcardsForLas31(playerIdx);
                        } else {
                            // For human player, bind wildcards automatically to complete Las Cosas
                            await bindAllPlayerWildcards(playerIdx);
                        }
                        // Show visual update after binding
                        await new Promise(r => setTimeout(r, GAME_TIMINGS.lasCosas.afterBindWildcard));
                    }

                    // 3. Show the "LAS COSAS" toast
                    safeShowPlayerToast('¡LAS COSAS!', playerIdx, { type: 'success', teamColor: playerTeamColor, duration: GAME_TIMINGS.lasCosas.toastDuration });

                    // 4. Wait a moment before revealing all cards and awarding points
                    await new Promise(r => setTimeout(r, GAME_TIMINGS.lasCosas.beforeRevealAll));

                    // 5. Reveal all player cards for visual verification
                    await dealManagerRendererRef.current.revealAllPlayerCards();

                    // 6. Award points (this may trigger win)
                    awardPoints(playerIdx, 18, 'Las Cosas');

                    // 7. Wait for players to see the result before ending
                    await new Promise(r => setTimeout(r, GAME_TIMINGS.lasCosas.afterVictory));

                    endBacigaHand();
                    return;
                }

                if (score.points > 31) {
                    // Cancel any pending score toast - "PASADO" is what matters
                    if (pendingScoreToastTimeoutsRef.current[playerIdx]) {
                        clearTimeout(pendingScoreToastTimeoutsRef.current[playerIdx]);
                        pendingScoreToastTimeoutsRef.current[playerIdx] = null;
                    }

                    // Reveal covered card if exists before showing PASADO toast
                    const coverState = cardCoverStateRef.current[playerIdx] || { coveredCardIndex: null };
                    if (coverState && coverState.coveredCardIndex !== null) {
                        await dealManagerRendererRef.current.revealCoveredCard(playerIdx, coverState.coveredCardIndex);
                        const newState = { ...coverState, coveredCardIndex: null };
                        setCardCoverState(prev => ({ ...prev, [playerIdx]: newState }));
                        cardCoverStateRef.current[playerIdx] = newState;
                    }

                    const teamId = (playerIdx === 0 || playerIdx === 2) ? 1 : 2;
                    const playerTeamColor = teamId === 1 ? teamColors.team1.hex : teamColors.team2.hex;
                    const marker = teamId === 1 ? team1Marker : team2Marker;

                    // Check if player had their báciga VALIDATED (already scored)
                    const hadValidatedBaciga = validatedBacigasRef.current.has(playerIdx);

                    if (hadValidatedBaciga) {
                        // Player with VALIDATED báciga went bust: lose 2 for báciga + 1 for bust = -3 total
                        safeShowPlayerToast('¡PASADO! (Báciga -2, Pasado -1)', playerIdx,
                            { type: 'error', teamColor: playerTeamColor, duration: 2500 });
                        marker.subtractPoints(3);
                    } else {
                        // Normal bust (no validated báciga): -1 point
                        safeShowPlayerToast('¡PASADO!', playerIdx,
                            { type: 'error', teamColor: playerTeamColor, duration: 2000 });
                        marker.subtractPoints(1);
                    }

                    // Wait for bust toast to finish
                    await new Promise(r => setTimeout(r, hadValidatedBaciga ? 2500 : 2000));

                    // Award bonus points to rival team ONLY if the player who busted had a VALIDATED báciga
                    // When a player with validated báciga busts:
                    // 1. Rivals with validated bácigas get +2 bonus
                    // 2. Rivals who DECLARED báciga but lost the comparison now get their +2 points
                    //    (since the winner busted, their báciga becomes valid)
                    if (hadValidatedBaciga) {
                        const rivalTeamId = teamId === 1 ? 2 : 1;
                        const rivalPlayers = rivalTeamId === 1 ? [0, 2] : [1, 3];
                        const rivalTeamColor = rivalTeamId === 1 ? teamColors.team1.hex : teamColors.team2.hex;

                        // Find rivals who DECLARED baciga (even if not validated)
                        const currentDeclarations = declarationsRef.current || [];
                        const rivalsWhoDeclaredBaciga = rivalPlayers.filter(idx =>
                            currentDeclarations.some(d => d.playerIndex === idx && (d.type === 'baciga' || d.type === 'bacigote'))
                        );

                        // Rivals with validated bácigas (already have their points, get bonus)
                        const rivalsWithValidatedBaciga = rivalPlayers.filter(idx =>
                            validatedBacigasRef.current.has(idx)
                        );

                        // Rivals who declared but were NOT validated (lost comparison, now win because rival busted)
                        const rivalsWhoLostButNowWin = rivalsWhoDeclaredBaciga.filter(idx =>
                            !validatedBacigasRef.current.has(idx)
                        );

                        // Award +2 to each rival with VALIDATED báciga (bonus because rival busted)
                        for (const rivalIdx of rivalsWithValidatedBaciga) {
                            safeShowPlayerToast(
                                `Rival se pasó (+2)`,
                                rivalIdx,
                                { type: 'success', teamColor: rivalTeamColor, duration: 2500 }
                            );

                            awardPoints(rivalIdx, 2, 'Rival se pasó');
                            await new Promise(r => setTimeout(r, 2500));
                        }

                        // Award +2 to rivals who declared báciga but lost comparison
                        // (they now "win" because the validated player busted)
                        for (const rivalIdx of rivalsWhoLostButNowWin) {
                            safeShowPlayerToast(
                                `¡Báciga! (Rival se pasó)`,
                                rivalIdx,
                                { type: 'success', teamColor: rivalTeamColor, duration: 2500 }
                            );

                            awardPoints(rivalIdx, 2, 'Báciga (rival se pasó)');
                            await new Promise(r => setTimeout(r, 2500));
                        }

                        if (rivalsWithValidatedBaciga.length > 0 || rivalsWhoLostButNowWin.length > 0) {
                            await new Promise(r => setTimeout(r, 500)); // Extra delay
                        }
                    }

                    // Hide AI player's cards after they bust (before passing to next player)
                    // Human player (0) keeps their cards visible
                    if (playerIdx !== 0) {
                        await dealManagerRendererRef.current.hidePlayerCards(playerIdx);
                    }

                    if (playerIdx === currentDealerIndexRef.current) {
                        end31Phase();
                    } else {
                        processTurn31((playerIdx + 1) % 4);
                    }
                } else {
                    // Check if this card came covered and player needs to decide
                    const state = cardCoverStateRef.current[playerIdx] || { coveredCardIndex: null };
                    if (state.coveredCardIndex !== null) {
                        if (playerIdx !== 0) {
                            // AI MUST reveal the covered card before asking for another
                            // This prevents having multiple cards face-down at once
                            debugLog(`🤖 [Baciga] AI Player ${playerIdx} must reveal covered card before continuing`);
                            await forceRevealCoveredCardForAI(playerIdx);
                            await new Promise(r => setTimeout(r, 1000));
                            processAiTurn31(playerIdx);
                        } else {
                            // Human player can decide to reveal or keep asking for cards
                            // Update player score and wildcards state after receiving new card
                            updatePlayerScoreAndWildcards();
                            setShowVoteButtons(true);
                        }
                    } else {
                        // No covered card, continue normally
                        if (playerIdx !== 0) {
                            await new Promise(r => setTimeout(r, 1000));
                            processAiTurn31(playerIdx);
                        } else {
                            // Update player score and wildcards state after receiving new card
                            updatePlayerScoreAndWildcards();
                            setShowVoteButtons(true);
                        }
                    }
                }
            } else {
                // Deck is truly empty with no cards to recycle - prevent game freeze
                debugLog(`⚠️ [Baciga] Mazo vacío al pedir carta (jugador ${playerIdx}), forzando planto`);
                const playerTeamId = (playerIdx === 0 || playerIdx === 2) ? 1 : 2;
                const playerTeamColor = playerTeamId === 1 ? teamColors.team1.hex : teamColors.team2.hex;
                safeShowPlayerToast('Sin cartas - Me planto', playerIdx, { type: 'warning', teamColor: playerTeamColor, duration: 1500 });
                await new Promise(r => setTimeout(r, 1500));
                if (playerIdx === currentDealerIndexRef.current) {
                    end31Phase();
                } else {
                    processTurn31((playerIdx + 1) % 4);
                }
            }
        }
    };

    /**
     * Handle revealing the covered card for human player
     * Note: Human player ALREADY sees their own card, this reveals it to AI players
     * After revealing, all future cards will come face-up (visible to everyone)
     */
    const handleRevealCoveredCard = async () => {
        const playerIdx = 0;
        const state = cardCoverStateRef.current[playerIdx] || { coveredCardIndex: null };

        if (state.coveredCardIndex === null) return;

        debugLog(`🎮 [Baciga] Player ${playerIdx} revealing covered card to others (already knows own card)`);

        // Cancel any pending score toast that was waiting to show "y la tapada"
        if (pendingScoreToastTimeoutsRef.current[playerIdx]) {
            clearTimeout(pendingScoreToastTimeoutsRef.current[playerIdx]);
            pendingScoreToastTimeoutsRef.current[playerIdx] = null;
            debugLog(`🎮 [Baciga] Cancelled pending "tapada" toast, will show full sum instead`);
        }

        // Remove the visual indicator (alpha and tint) from the card
        await dealManagerRendererRef.current.revealCoveredCard(playerIdx, state.coveredCardIndex);

        // Update state FIRST - clear covered card index so score calculation reflects revealed state
        const newState = {
            ...state,
            coveredCardIndex: null
        };
        setCardCoverState(prev => ({ ...prev, [playerIdx]: newState }));
        cardCoverStateRef.current[playerIdx] = newState;

        // Calculate and show the FULL score now that card is revealed
        const cards = dealManagerRef.current.getPlayerCards(playerIdx);
        const score = bacigaLogicRef.current.calculateScore(cards);

        // Check for unbound comodines
        const unboundComodines = cards.filter(card =>
            bacigaLogicRef.current.isComodin(card) && !bacigaLogicRef.current.isCardBound(card)
        );

        // Calculate full sum (no covered cards now)
        let fullSum = 0;
        cards.forEach((card) => {
            const isUnboundComodin = bacigaLogicRef.current.isComodin(card) &&
                                    !bacigaLogicRef.current.isCardBound(card);
            if (!isUnboundComodin) {
                fullSum += bacigaLogicRef.current.getCardValue(card);
            }
        });

        // Build score message without "tapada"
        let scoreMessage = '';
        const hasUnboundComodines = unboundComodines.length > 0;

        if (hasUnboundComodines) {
            scoreMessage = `${fullSum} y ${unboundComodines.length === 1 ? 'el comodín' : 'los comodines'}`;
        } else {
            scoreMessage = `${fullSum}`;
        }

        // Show the full score toast
        const playerTeamColor = teamColors.team1.hex;
        safeShowPlayerToast(scoreMessage, playerIdx, {
            type: 'info',
            teamColor: playerTeamColor,
            duration: 2000
        });

        debugLog(`🎮 [Baciga] Player ${playerIdx} revealed card, future cards will come face-up`);
    };

    /**
     * Force AI to reveal covered card (used when AI requests another carta)
     * This prevents having multiple cards face-down at once
     *
     * @param {number} playerIdx - AI player index
     */
    const forceRevealCoveredCardForAI = async (playerIdx) => {
        const state = cardCoverStateRef.current[playerIdx] || { coveredCardIndex: null };
        const cards = dealManagerRef.current.getPlayerCards(playerIdx);

        if (state.coveredCardIndex === null) return;

        const coveredCard = cards[state.coveredCardIndex];
        if (!coveredCard) return;

        debugLog(`🤖 [Baciga] AI Player ${playerIdx} forced to reveal covered card (${coveredCard.value} ${coveredCard.suit})`);

        // Cancel any pending score toast
        if (pendingScoreToastTimeoutsRef.current[playerIdx]) {
            clearTimeout(pendingScoreToastTimeoutsRef.current[playerIdx]);
            pendingScoreToastTimeoutsRef.current[playerIdx] = null;
        }

        // Reveal the card visually
        await dealManagerRendererRef.current.revealCoveredCard(playerIdx, state.coveredCardIndex);

        // Update state
        const newState = {
            ...state,
            coveredCardIndex: null
        };
        setCardCoverState(prev => ({ ...prev, [playerIdx]: newState }));
        cardCoverStateRef.current[playerIdx] = newState;

        // Show toast with total score
        const score = bacigaLogicRef.current.calculateScore(cards);
        safeShowPlayerToast(`${score.points}`, playerIdx, { duration: 2000 });

        debugLog(`🤖 [Baciga] AI Player ${playerIdx} revealed card, future cards will come face-up`);
    };

    /**
     * AI decides whether to reveal the covered card to other players
     * Note: AI ALWAYS knows its own card value, this decision is about revealing it to others
     *
     * Strategy probabilities:
     * - If covered card is a wildcard: 70% reveal (to show strength/flexibility)
     * - If makes a pair/trio: 100% reveal (to score points)
     * - If good hand (25+ pts): 80% reveal (confident in hand)
     * - If bad hand (<20 pts): 30% reveal (hide weakness, hope for better cards)
     * - Medium hands: 50/50 (balanced strategy)
     *
     * @param {number} playerIdx - AI player index
     */
    const aiDecideRevealCoveredCard = async (playerIdx) => {
        const state = cardCoverStateRef.current[playerIdx] || { coveredCardIndex: null };
        const cards = dealManagerRef.current.getPlayerCards(playerIdx);

        if (state.coveredCardIndex === null) return;

        const coveredCard = cards[state.coveredCardIndex];

        // AI ALWAYS knows its card value, just deciding whether to reveal to human player
        const score = bacigaLogicRef.current.calculateScore(cards);

        let shouldReveal = false;

        if (bacigaLogicRef.current.isComodin(coveredCard)) {
            // 70% reveal wildcards - shows strength and flexibility
            shouldReveal = Math.random() > 0.3;
        } else {
            // Check if this card forms pairs/trios
            const cardValue = coveredCard.value;
            const matchingCards = cards.filter(c => c.value === cardValue).length;

            if (matchingCards >= 2) {
                // Always reveal pairs/trios - need to show for scoring
                shouldReveal = true;
            } else if (score.points >= 25) {
                // 80% reveal good hands - confident strategy
                shouldReveal = Math.random() > 0.2;
            } else if (score.points < 20) {
                // 30% reveal bad hands - hide weakness
                shouldReveal = Math.random() > 0.7;
            } else {
                // 50/50 for medium hands - balanced
                shouldReveal = Math.random() > 0.5;
            }
        }

        if (shouldReveal) {
            debugLog(`🤖 [Baciga] AI Player ${playerIdx} revealing covered card to others (card: ${coveredCard.value} ${coveredCard.suit}, score: ${score.points})`);

            // Cancel any pending score toast that was waiting to show "y la tapada"
            if (pendingScoreToastTimeoutsRef.current[playerIdx]) {
                clearTimeout(pendingScoreToastTimeoutsRef.current[playerIdx]);
                pendingScoreToastTimeoutsRef.current[playerIdx] = null;
                debugLog(`🤖 [Baciga] Cancelled pending "tapada" toast for AI, will show full sum instead`);
            }

            // Reveal the card visually to the human player
            await dealManagerRendererRef.current.revealCoveredCard(playerIdx, state.coveredCardIndex);

            const newState = {
                ...state,
                coveredCardIndex: null
            };
            setCardCoverState(prev => ({ ...prev, [playerIdx]: newState }));
            cardCoverStateRef.current[playerIdx] = newState;

            // Calculate and show the FULL score now that card is revealed
            const updatedCards = dealManagerRef.current.getPlayerCards(playerIdx);
            const updatedScore = bacigaLogicRef.current.calculateScore(updatedCards);

            // Check for unbound comodines
            const unboundComodines = updatedCards.filter(card =>
                bacigaLogicRef.current.isComodin(card) && !bacigaLogicRef.current.isCardBound(card)
            );

            // Calculate full sum (no covered cards now)
            let fullSum = 0;
            updatedCards.forEach((card) => {
                const isUnboundComodin = bacigaLogicRef.current.isComodin(card) &&
                                        !bacigaLogicRef.current.isCardBound(card);
                if (!isUnboundComodin) {
                    fullSum += bacigaLogicRef.current.getCardValue(card);
                }
            });

            // Build score message without "tapada"
            let scoreMessage = '';
            const hasUnboundComodines = unboundComodines.length > 0;

            if (hasUnboundComodines) {
                scoreMessage = `${fullSum} y ${unboundComodines.length === 1 ? 'el comodín' : 'los comodines'}`;
            } else {
                scoreMessage = `${fullSum}`;
            }

            // Show the full score toast
            const playerTeamId = (playerIdx === 0 || playerIdx === 2) ? 1 : 2;
            const playerTeamColor = playerTeamId === 1 ? teamColors.team1.hex : teamColors.team2.hex;
            safeShowPlayerToast(scoreMessage, playerIdx, {
                type: 'info',
                teamColor: playerTeamColor,
                duration: 2000
            });
        } else {
            debugLog(`🤖 [Baciga] AI Player ${playerIdx} keeping card covered from others (card: ${coveredCard.value} ${coveredCard.suit}, score: ${score.points})`);
        }
    };

    const end31Phase = async () => {
        setPhase('roundEnd');
        debugLog('🏁 [Baciga] end31Phase - calculating final scores');

        // Check if game already ended (one team reached 18 points)
        if (team1Marker.score >= 18 || team2Marker.score >= 18) {
            debugLog('🏆 [Baciga] Game already ended, skipping end31Phase scoring');
            return;
        }

        // First, reveal all covered cards
        debugLog('🏁 [Baciga] Revealing all covered cards');
        for (let playerIdx = 0; playerIdx < 4; playerIdx++) {
            const state = cardCoverStateRef.current[playerIdx];
            if (state && state.coveredCardIndex !== null) {
                await dealManagerRendererRef.current.revealCoveredCard(playerIdx, state.coveredCardIndex);
            }
        }

        // Clear card cover state
        setCardCoverState({});
        cardCoverStateRef.current = {};

        // Reposition all player cards to group ligues now that all cards are visible
        debugLog('🏁 [Baciga] Repositioning cards to show ligues');
        for (let playerIdx = 0; playerIdx < 4; playerIdx++) {
            await dealManagerRendererRef.current.repositionPlayerCardsFor31(playerIdx);
        }

        await new Promise(r => setTimeout(r, 500));

        const manoPlayerIdx = (currentDealerIndexRef.current + 1) % 4;

        // Create turn order array starting from mano and ending with dealer
        const turnOrder = [];
        for (let i = 0; i < 4; i++) {
            turnOrder.push((manoPlayerIdx + i) % 4);
        }
        debugLog(`📊 [Baciga] Scoring in turn order:`, turnOrder);

        // 1. Bácigas were already validated during the 31 phase (in turn order)
        debugLog('📊 [Baciga] Bácigas already validated during 31 phase, skipping');

        // 2. NEW FLOW: Each player announces their score in turn order
        const playerScores = []; // Will store { playerIdx, points, busted, liguesPoints, pairsInfo }

        safeShowToast(`${TOAST_ICONS.INFO} Conteo de puntos y ligues`, { type: 'info', duration: 2000 });
        await new Promise(r => setTimeout(r, 2000));

        for (const playerIdx of turnOrder) {
            await processPlayerScoreAnnouncement(playerIdx, playerScores);

            // Check if game ended after this player's announcement
            if (team1Marker.score >= 18 || team2Marker.score >= 18) {
                debugLog('🏆 [Baciga] Game ended during score announcements, skipping remaining players and Las 31 scoring');
                return;
            }
        }

        // 3. After all players have announced, determine winner of "Las 31" or "El Punto"
        const validScores = playerScores.filter(s => !s.busted);
        const thirtyOnes = validScores.filter(s => s.points === 31);

        if (thirtyOnes.length > 0) {
            // First player in turn order with 31 wins
            const winner = thirtyOnes[0];
            const winnerName = players[winner.playerIdx].name;
            const winnerTeamId = (winner.playerIdx === 0 || winner.playerIdx === 2) ? 1 : 2;
            const winnerTeamColor = winnerTeamId === 1 ? teamColors.team1.hex : teamColors.team2.hex;
            safeShowPlayerToast(`¡${winnerName} gana con Las 31! (+2)`, winner.playerIdx, { type: 'success', teamColor: winnerTeamColor, duration: 2500 });
            await awardPoints(winner.playerIdx, 2, 'Las 31');
            await new Promise(r => setTimeout(r, SCORE_COUNT_SPEED));
        } else if (validScores.length > 0) {
            // Highest score wins (with turn order as tiebreaker)
            validScores.sort((a, b) => {
                if (b.points !== a.points) return b.points - a.points;
                // a comes before b in turn order, so a wins
                return 0; // Already sorted by turn order
            });
            const winner = validScores[0];
            const winnerName = players[winner.playerIdx].name;
            const winnerTeamId = (winner.playerIdx === 0 || winner.playerIdx === 2) ? 1 : 2;
            const winnerTeamColor = winnerTeamId === 1 ? teamColors.team1.hex : teamColors.team2.hex;
            safeShowPlayerToast(`¡${winnerName} gana el Punto con ${winner.points}! (+1)`, winner.playerIdx, { type: 'success', teamColor: winnerTeamColor, duration: 2500 });
            await awardPoints(winner.playerIdx, 1, 'El Punto');
            await new Promise(r => setTimeout(r, SCORE_COUNT_SPEED));
        }

        await new Promise(r => setTimeout(r, 500));
        endBacigaHand();
    };

    /**
     * Process a player's score announcement during end31Phase
     * This includes binding wildcards if needed, announcing points and ligues
     * @param {number} playerIdx - Player index (0-3)
     * @param {Array} playerScores - Array to store score results (used to check if someone has 31)
     */
    const processPlayerScoreAnnouncement = async (playerIdx, playerScores) => {
        const playerTeamId = (playerIdx === 0 || playerIdx === 2) ? 1 : 2;
        const playerTeamColor = playerTeamId === 1 ? teamColors.team1.hex : teamColors.team2.hex;
        const playerName = players[playerIdx].name;

        // Check if someone already has exactly 31 in this round
        const someoneHas31 = playerScores.some(s => !s.busted && s.points === 31);

        debugLog(`📊 [Baciga] Processing score announcement for player ${playerIdx} (${playerName}), someoneHas31: ${someoneHas31}`);

        // Reveal AI player's cards at the start of their score announcement
        // (cards were hidden when they finished their turn)
        if (playerIdx !== 0) {
            await revealCards(playerIdx);
            await new Promise(r => setTimeout(r, 300));
        }

        // Reposition cards to group ligues (cards of same value) for this player
        await dealManagerRendererRef.current.repositionPlayerCardsFor31(playerIdx);
        await new Promise(r => setTimeout(r, 300));

        // Check if player has unbound wildcards
        let cards = dealManagerRef.current.getPlayerCards(playerIdx);
        const hasUnboundWildcards = cards.some(c =>
            bacigaLogicRef.current.isComodin(c) && !bacigaLogicRef.current.isCardBound(c)
        );

        // If player has unbound wildcards, they must bind them now
        if (hasUnboundWildcards) {
            if (playerIdx === 0) {
                // Human player - open wildcard selector
                safeShowToast(`${TOAST_ICONS.WARNING} Debes amarrar tus comodines para anunciar tu puntuación`,
                    { type: 'warning', duration: 3000 });
                await new Promise(r => setTimeout(r, 1000));

                // Bind all wildcards sequentially
                await bindAllPlayerWildcards(playerIdx);

                // Get updated cards after binding
                cards = dealManagerRef.current.getPlayerCards(playerIdx);
            } else {
                // AI player - bind wildcards automatically for Las 31 scoring
                // Pass someoneHas31 to help AI decide optimal strategy
                await assignAiWildcardsForLas31(playerIdx, someoneHas31);
                cards = dealManagerRef.current.getPlayerCards(playerIdx);

                // Show what AI assigned to wildcards
                cards.forEach(card => {
                    if (bacigaLogicRef.current.isComodin(card) && bacigaLogicRef.current.isCardBound(card)) {
                        const val = card.assignedValue;
                        let label = val;
                        if (val === 1) label = 'As';
                        else if (val === 10) label = 'Sota';
                        else if (val === 11) label = 'Caballo';
                        else if (val === 12) label = 'Rey';

                        safeShowPlayerToast(`Comodín = ${label}`, playerIdx,
                            { type: 'info', teamColor: playerTeamColor, duration: 2000 });
                    }
                });
                await new Promise(r => setTimeout(r, 2000));
            }

            // Reposition cards again after binding wildcards (may form new ligues)
            await dealManagerRendererRef.current.repositionPlayerCardsFor31(playerIdx);
            await new Promise(r => setTimeout(r, 300));
        } else if (playerIdx !== 0) {
            // AI player has no unbound wildcards - but may have wildcards already bound during voting (báciga)
            // Show their pre-bound wildcard values for transparency
            cards.forEach(card => {
                if (bacigaLogicRef.current.isComodin(card) && bacigaLogicRef.current.isCardBound(card)) {
                    const val = card.assignedValue;
                    let label = val;
                    if (val === 1) label = 'As';
                    else if (val === 10) label = 'Sota';
                    else if (val === 11) label = 'Caballo';
                    else if (val === 12) label = 'Rey';
                    safeShowPlayerToast(`Comodín fijado en ${label} (anteriormente en la báciga)`, playerIdx,
                        { type: 'info', teamColor: playerTeamColor, duration: 2000 });
                }
            });
            const hasBoundComodines = cards.some(card =>
                bacigaLogicRef.current.isComodin(card) && bacigaLogicRef.current.isCardBound(card)
            );
            if (hasBoundComodines) {
                await new Promise(r => setTimeout(r, 2000));
            }
        }

        // Calculate final score
        const score = bacigaLogicRef.current.calculateScore(cards);
        const isBusted = score.points > 31;

        debugLog(`📊 [Baciga] Player ${playerIdx} final score: ${score.points}${isBusted ? ' (BUSTED)' : ''}`);

        // Announce points
        if (isBusted) {
            safeShowPlayerToast('Me he pasado, no me apunto nada', playerIdx,
                { type: 'error', teamColor: playerTeamColor, duration: 2000 });
            await new Promise(r => setTimeout(r, 2000));

            playerScores.push({ playerIdx, points: 0, busted: true, liguesPoints: 0, pairsInfo: null });
            return;
        }

        // Announce points
        safeShowPlayerToast(`Tengo ${score.points} puntos`, playerIdx,
            { type: 'info', teamColor: playerTeamColor, duration: 2000 });
        await new Promise(r => setTimeout(r, 2000));

        // Check if game ended (one team reached 18 points)
        if (team1Marker.score >= 18 || team2Marker.score >= 18) {
            debugLog('🏆 [Baciga] Game already ended, skipping ligues scoring');
            playerScores.push({
                playerIdx,
                points: score.points,
                busted: false,
                liguesPoints: 0,
                pairsInfo: null
            });
            return;
        }

        // Calculate and announce ligues
        const pairsInfo = bacigaLogicRef.current.countPairsAndTrios(cards);

        if (pairsInfo.points > 0) {
            let desc = '';
            if (pairsInfo.trios > 0) desc += `${pairsInfo.trios} trío(s)`;
            if (pairsInfo.pairs > 0) {
                if (desc) desc += ' y ';
                desc += `${pairsInfo.pairs} ligue(s)`;
            }
            safeShowPlayerToast(`Tengo ${desc} (+${pairsInfo.points})`, playerIdx,
                { type: 'info', teamColor: playerTeamColor, duration: 2000 });

            // Award ligues points immediately
            await awardPoints(playerIdx, pairsInfo.points, 'Ligues/Tríos');
            await new Promise(r => setTimeout(r, 2000));

            // Check again if game ended after awarding ligues
            if (team1Marker.score >= 18 || team2Marker.score >= 18) {
                debugLog('🏆 [Baciga] Game ended after awarding ligues, stopping score announcements');
                playerScores.push({
                    playerIdx,
                    points: score.points,
                    busted: false,
                    liguesPoints: pairsInfo.points,
                    pairsInfo
                });
                return;
            }
        } else {
            safeShowPlayerToast('No tengo ligues', playerIdx,
                { type: 'info', teamColor: playerTeamColor, duration: 1500 });
            await new Promise(r => setTimeout(r, 1500));
        }

        playerScores.push({
            playerIdx,
            points: score.points,
            busted: false,
            liguesPoints: pairsInfo.points,
            pairsInfo
        });
    };

    /**
     * Bind all unbound wildcards for a player sequentially
     * For human player, opens wildcard selector for each one
     * @param {number} playerIdx - Player index (must be 0 for human)
     */
    const bindAllPlayerWildcards = async (playerIdx) => {
        if (playerIdx !== 0) return; // Only for human player

        let cards = dealManagerRef.current.getPlayerCards(playerIdx);
        let unboundWildcards = cards.filter(c =>
            bacigaLogicRef.current.isComodin(c) && !bacigaLogicRef.current.isCardBound(c)
        );

        while (unboundWildcards.length > 0) {
            const unboundIdx = cards.findIndex(c =>
                bacigaLogicRef.current.isComodin(c) && !bacigaLogicRef.current.isCardBound(c)
            );
            const unboundCard = cards[unboundIdx];

            // Open selector and wait for user selection
            await new Promise((resolve) => {
                // Set flag to indicate we're in score announcement phase
                setPendingCounterDeclaration('scoreAnnouncement');

                setWildcardSelector({
                    open: true,
                    playerIdx: 0,
                    cardIdx: unboundIdx,
                    card: unboundCard,
                    currentValue: unboundCard.assignedValue || null,
                    onComplete: resolve // Callback when binding is complete
                });
            });

            // Refresh cards after binding
            cards = dealManagerRef.current.getPlayerCards(playerIdx);
            unboundWildcards = cards.filter(c =>
                bacigaLogicRef.current.isComodin(c) && !bacigaLogicRef.current.isCardBound(c)
            );
        }

        setPendingCounterDeclaration(null);
    };

    /**
     * Check if player can win with current ligues in hand
     * Called during 31 phase before player requests a card
     * @param {number} playerIdx - Player index (0-3)
     * @returns {Object|null} - { canWin: true, liguesPoints, pairsInfo, hasUnboundWildcards } or null
     */
    const checkLiguesWinCondition = (playerIdx) => {
        const cards = dealManagerRef.current.getPlayerCards(playerIdx);

        // Check if there are unbound wildcards
        const hasUnboundWildcards = cards.some(c =>
            bacigaLogicRef.current.isComodin(c) && !bacigaLogicRef.current.isCardBound(c)
        );

        // countPairsAndTrios automatically assigns unbound wildcards to the best group
        const pairsInfo = bacigaLogicRef.current.countPairsAndTrios(cards);

        if (pairsInfo.points === 0) {
            return null; // No ligues
        }

        const teamId = (playerIdx === 0 || playerIdx === 2) ? 1 : 2;
        const marker = teamId === 1 ? team1Marker : team2Marker;
        const currentScore = marker.score;

        if (currentScore + pairsInfo.points >= 18) {
            return {
                canWin: true,
                liguesPoints: pairsInfo.points,
                pairsInfo: pairsInfo,
                hasUnboundWildcards: hasUnboundWildcards
            };
        }

        return null;
    };

    const awardPoints = async (playerIdx, points, reason) => {
        const teamId = (playerIdx === 0 || playerIdx === 2) ? 1 : 2;
        const marker = teamId === 1 ? team1Marker : team2Marker;
        await marker.addPoints(points);

        // History & Stats
        let actor = 'Pareja Rival';
        let statActor = 'rivals';
        if (playerIdx === 0) { actor = 'Jugador'; statActor = 'player'; }
        else if (playerIdx === 2) { actor = 'Compañero'; statActor = 'partner'; }

        marker.addHistoryEntry(reason, actor, points, handNumber);

        const rLower = reason.toLowerCase();
        let actionKey = null;
        if (rLower.includes('baciga')) actionKey = 'bacigas';
        else if (rLower.includes('bacigote')) actionKey = 'bacigotes';
        else if (rLower.includes('31') || rLower.includes('punto')) actionKey = 'las31';
        else if (rLower.includes('cosas')) actionKey = 'cosas';

        if (actionKey) statsService.registerAction(actionKey, statActor);
        if (statActor === 'player') statsService.registerPoints(points, 'player');
        achievementsService.checkAchievements(statsService.getStats());
    };

    const [, forceUpdate] = useState(); // Trigger re-render to update score header if connected to markers



    // Add effect to listen to ScoreMarker wins
    useEffect(() => {
        const handleWin = async (winningTeamId) => {
            // Mark that a round was won - this prevents endBacigaHand from running automatically
            roundWonRef.current = true;

            // Game Won!
            globalMatchScore.addWin(winningTeamId);
            const currentScores = globalMatchScore.getScores();
            setGlobalScore(currentScores);

            // Register stats
            statsService.registerGameStart('baciga');
            if (winningTeamId === 1) {
                statsService.registerWin('baciga');
                if (team2Marker.score === 0) statsService.registerPerfectGameBaciga();
                if (wasPlayerTeamBehindRef.current) statsService.registerComeback();
            }
            wasPlayerTeamBehindRef.current = false;
            achievementsService.checkAchievements(statsService.getStats());

            debugLog(`🏆 [Baciga] Hand Won by Team ${winningTeamId}. Match Score:`, currentScores, 'Target Pts:', gamePoints);

            // Stop any ongoing turn processing (no need for setIsAIThinking in Baciga)

            // Check Match Win
            const teamWins = winningTeamId === 1 ? currentScores.team1 : currentScores.team2;

            if (teamWins >= gamePoints) { // Match Won
                setWinnerTeamId(winningTeamId);
                setShowWinnerModal(true);
            } else {
                // Store transition info and show confirmation modal instead of auto-transitioning
                const nextRoute = gameMode === 'classic' ? '/ronda' : '/baciga';
                const nextGameName = gameMode === 'classic' ? 'Ronda' : 'Báciga';

                setPendingNextGame({
                    nextRoute,
                    nextGameName,
                    winningTeamId,
                    currentScores
                });
                setRoundWinnerTeamId(winningTeamId);
                setShowRoundWinnerModal(true);
            }
        };

        team1Marker.setOnWin(handleWin);
        team2Marker.setOnWin(handleWin);
    }, [globalMatchScore, gamePoints, gameMode, navigate, players]);

    // Handler for when user confirms round win and wants to continue to next game
    const handleRoundWinnerContinue = async () => {
        if (!pendingNextGame) return;

        const { nextRoute, winningTeamId } = pendingNextGame;

        debugLog('🎮 [Baciga] User confirmed round win, proceeding with cleanup and transition');

        // Close the modal first
        setShowRoundWinnerModal(false);

        // Cancel any card selection in progress (flip cards back to front)
        if (selectedCardIndices.length > 0) {
            debugLog('🃏 [Baciga] Canceling card selection before hand end');
            await handleCancelSelection();
        }

        // Animate all cards returning to deck
        if (dealManagerRendererRef.current && deckRendererRef.current) {
            const deckPosition = deckRendererRef.current.getDeckPosition();
            await dealManagerRendererRef.current.animateCardsReturnToDeck(deckPosition);
        }

        // Reset and shuffle deck
        if (deckRef.current) {
            deckRef.current.reset();
            deckRef.current.shuffle();
            debugLog('🎲 [Baciga] Deck reset and shuffled');
        }

        // Reset deal manager
        if (dealManagerRef.current) {
            dealManagerRef.current.reset();
        }

        // Re-render the deck to show full deck again
        if (deckRendererRef.current) {
            await deckRendererRef.current.render();
        }

        // Reset score markers for next game
        team1Marker.reset();
        team2Marker.reset();
        debugLog('🔄 [Baciga] Score markers reset');

        // Next Game
        if (gameMode === 'classic') {
            // Classic mode: navigate to Ronda
            navigate(nextRoute, {
                state: {
                    players, gamePoints, gameMode,
                    globalScore: globalMatchScore.getScores(),
                    startingDealer: players[(currentDealerIndexRef.current + 1) % 4] // Rotate dealer for NEXT game
                }
            });
        } else {
            // Baciga only mode: start new hand in same component
            // Reset roundWonRef BEFORE calling endBacigaHand so it doesn't get blocked
            roundWonRef.current = false;
            await endBacigaHand();
        }

        // Clear pending state
        setPendingNextGame(null);
        setRoundWinnerTeamId(null);
        
        // Reset the round won flag so future hands work correctly
        roundWonRef.current = false;
    };

    const endBacigaHand = async () => {
        // If a round was just won, the modal is handling the transition - don't proceed
        if (roundWonRef.current) {
            debugLog('⚠️ [Baciga] endBacigaHand skipped - round was won, waiting for modal confirmation');
            return;
        }
        
        // Prevent multiple simultaneous calls
        if (isEndingHandRef.current) {
            debugLog('⚠️ [Baciga] endBacigaHand already in progress, skipping duplicate call');
            return;
        }

        isEndingHandRef.current = true;

        try {
            // If no one won the game, rotate dealer and start new hand
            await new Promise(r => setTimeout(r, 2000));
            const nextDealer = (currentDealerIndexRef.current + 1) % 4;
            setCurrentDealerIndex(nextDealer);
            currentDealerIndexRef.current = nextDealer;

            // Reset hand number to 1 when dealer changes (new reparto)
            // This ensures players must pass at least once before calling "Cartas"
            setHandNumber(1);
            handNumberRef.current = 1;

            debugLog(`🎲 [Baciga] Starting hand #1 with new dealer ${players[nextDealer].name}`);

            // Reset deck completely (reinitialize all 40 cards and shuffle)
            debugLog('🎲 [Baciga] Resetting and shuffling deck for new hand');
            deckRef.current.reset(); // This reinitializes all 40 cards and shuffles
            dealManagerRef.current.reset(); // Clears internal hands logic

            // Re-render deck at new dealer position
            if (appRef.current && appRef.current.screen) {
                deckRendererRef.current.setPositionByPlayer(nextDealer, appRef.current.screen.width, appRef.current.screen.height);
                await deckRendererRef.current.render();
            }

            // Clear visual cards
            dealManagerRendererRef.current.clearAllCards();

            // Reset gameRestored flag to allow torito modal in future hands
            // Reset ref first for synchronous access in startNewHand
            gameRestoredRef.current = false;
            setGameRestored(false);

            startNewHand(nextDealer, 1);
        } finally {
            // Reset flag after hand has started
            isEndingHandRef.current = false;
        }
    };

    /* --- Wildcard Interaction Logic --- */

    const handleCardClick = (playerIdx, cardIdx, card) => {
        // Only allow interaction for human player (index 0)
        if (playerIdx !== 0) return;

        // Check if it's a wildcard
        const isComodin = bacigaLogicRef.current.isComodin(card);
        if (!isComodin) {
            debugLog('🃏 [Baciga] Clicked regular card:', card.value, card.suit);
            // If we are in card selection mode (passing), toggle selection
            if (isSelectingCards) {
                handleToggleCardSelection(cardIdx);
            }
            return;
        }

        // If card is already bound, do NOT allow changing it in Phase 31
        if (bacigaLogicRef.current.isCardBound(card)) {
            debugLog('🃏 [Baciga] Clicked bound wildcard - locked.');
            safeShowToast(`${TOAST_ICONS.WILDCARD} Comodín amarrado${SEPARATOR}${card.assignedValue}`, { type: 'warning', duration: 1500 });
            return;
        }

        debugLog('🃏 [Baciga] Clicked Wildcard!', card);

        setWildcardSelector({
            open: true,
            playerIdx,
            cardIdx,
            card,
            currentValue: card.assignedValue
        });
    };

    // Helper function to open wildcard selector for unbound wildcards
    const handleOpenWildcardSelector = () => {
        if (!dealManagerRef.current?.playerCards?.[0]) {
            console.error('🎴 [Baciga] No player cards found');
            return;
        }

        const playerHand = dealManagerRef.current.playerCards[0];
        const unboundWildcard = playerHand.find(card =>
            bacigaLogicRef.current.isComodin(card) && !bacigaLogicRef.current.isCardBound(card)
        );

        if (unboundWildcard) {
            const cardIdx = playerHand.indexOf(unboundWildcard);
            debugLog('🎴 [Baciga] Opening wildcard selector for card:', unboundWildcard, 'at index:', cardIdx);
            setWildcardSelector({
                open: true,
                playerIdx: 0,
                cardIdx,
                card: unboundWildcard,
                currentValue: unboundWildcard.assignedValue
            });
        }
    };

    // Helper function for torito decision
    const handleToritoDecision = (useTorito) => {
        debugLog('🎮 [Baciga] Torito decision:', useTorito);
        window.toritoDecisionResult = useTorito;
        if (window.resolveToritoDecision) {
            window.resolveToritoDecision();
        }
    };

    // Helper function to update player score and wildcard state
    const updatePlayerScoreAndWildcards = () => {
        debugLog('🎴 [Baciga] updatePlayerScoreAndWildcards called');

        if (!dealManagerRef.current?.playerCards?.[0]) {
            debugLog('🎴 [Baciga] No hand found for player 0');
            return null;
        }

        const cards = dealManagerRef.current.getPlayerCards(0);
        debugLog('🎴 [Baciga] Player 0 cards:', cards);

        if (!cards || cards.length === 0) {
            debugLog('🎴 [Baciga] Player 0 has no cards');
            return null;
        }

        const score = bacigaLogicRef.current.calculateScore(cards);
        const hasUnbound = cards.some(card =>
            bacigaLogicRef.current.isComodin(card) && !bacigaLogicRef.current.isCardBound(card)
        );
        debugLog('🎴 [Baciga] Updating player score and wildcards:', { score, hasUnbound, cards: cards.map(c => `${c.value} ${c.suit}`) });
        setPlayerScore(score);
        setHasUnboundWildcard(hasUnbound);
        return score;
    };

    const handleWildcardSelect = async (value) => {
        debugLog('🃏 [Baciga] Wildcard value selected:', value);
        const { playerIdx, cardIdx } = wildcardSelector;

        if (playerIdx !== null && cardIdx !== null) {
            // Update logic
            const success = dealManagerRef.current.updateCardData(playerIdx, cardIdx, { assignedValue: value });

            if (!success) {
                // Wildcard is already bound (e.g. bound during voting for báciga declaration).
                // Re-binding is not allowed within the same hand.
                const existingValue = dealManagerRef.current.playerCards[playerIdx]?.[cardIdx]?.assignedValue;
                const label = existingValue === 1 ? 'As' : existingValue === 10 ? 'Sota' : existingValue === 11 ? 'Caballo' : existingValue === 12 ? 'Rey' : existingValue;
                safeShowToast(`${TOAST_ICONS.WARNING} El comodín ya está fijado en ${label} desde la báciga`, { type: 'warning', duration: 3000 });
                setWildcardSelector({ ...wildcardSelector, open: false });
                // If there was a pending action, complete it with the already-bound wildcard
                if (pendingCounterDeclaration) {
                    const action = pendingCounterDeclaration;
                    const onCompleteCallback = wildcardSelector.onComplete;
                    setPendingCounterDeclaration(null);
                    if (action === 'scoreAnnouncement' && onCompleteCallback) {
                        onCompleteCallback();
                    } else if (action === 'continue31Turn') {
                        await processTurn31(0);
                    } else if (action === 'bindBeforeLas31') {
                        const summaryData = buildPhase31Summary();
                        setPhase31SummaryData(summaryData);
                        setShowPhase31SummaryModal(true);
                    } else {
                        await handleHumanCounterDeclaration(action);
                    }
                }
                return;
            }

            if (success) {
                // Update state to force re-render/logic-check
                // recalculate score for UI
                const score = updatePlayerScoreAndWildcards();

                safeShowToast(`${TOAST_ICONS.WILDCARD} Comodín asignado${SEPARATOR}${value}`, { type: 'warning', duration: 1500 });

                // Reposition cards to align with same-value cards during/after Las 31 phase
                if (dealManagerRendererRef.current && (phase === 'playing31' || pendingCounterDeclaration === 'scoreAnnouncement')) {
                    await dealManagerRendererRef.current.repositionPlayerCardsFor31(playerIdx);
                    debugLog('🎴 [Baciga] Cards repositioned after wildcard assignment for player', playerIdx);
                }

                // ** Check if this is during a pending counter-declaration or Las 31 turn **
                if (pendingCounterDeclaration) {
                    // Check if there are more unbound wildcards
                    const cards = dealManagerRef.current.getPlayerCards(0);
                    const remainingUnbound = cards.filter(c => bacigaLogicRef.current.isComodin(c) && !bacigaLogicRef.current.isCardBound(c));

                    if (remainingUnbound.length > 0) {
                        // Still have more wildcards to bind - open selector for next one
                        const nextUnboundIdx = cards.findIndex(c => bacigaLogicRef.current.isComodin(c) && !bacigaLogicRef.current.isCardBound(c));
                        const nextUnboundCard = cards[nextUnboundIdx];
                        setWildcardSelector({
                            open: true,
                            playerIdx: 0,
                            cardIdx: nextUnboundIdx,
                            card: nextUnboundCard,
                            currentValue: nextUnboundCard.assignedValue || null,
                            onComplete: wildcardSelector.onComplete // Preserve callback
                        });
                        return; // Wait for next wildcard binding
                    } else {
                        // All wildcards bound - complete the pending action
                        const action = pendingCounterDeclaration;
                        const onCompleteCallback = wildcardSelector.onComplete;

                        setWildcardSelector({ ...wildcardSelector, open: false, onComplete: null });
                        setPendingCounterDeclaration(null);

                        if (action === 'scoreAnnouncement') {
                            // During score announcement - just call the callback to continue
                            if (onCompleteCallback) {
                                onCompleteCallback();
                            }
                        } else if (action === 'continue31Turn') {
                            // Continue with Las 31 turn - reveal cards and proceed
                            await processTurn31(0);
                        } else if (action === 'bindBeforeLas31') {
                            // Wildcards bound before Las 31 phase - show summary modal
                            debugLog('🃏 [Baciga] Wildcards bound before Las 31, showing summary modal');
                            await new Promise(r => setTimeout(r, 1500));
                            const summaryData = buildPhase31Summary();
                            setPhase31SummaryData(summaryData);
                            setShowPhase31SummaryModal(true);
                        } else {
                            // Complete the counter-declaration (baciga or bacigote)
                            await handleHumanCounterDeclaration(action);
                        }
                        return;
                    }
                }

                // ** NEW LOGIC: Check for Las Cosas or Bust immediately after binding **

                // 1. Check for Las Cosas (MUST be <= 31 points)
                if (score && score.isCosas) {
                    if (score.points <= 31) {
                        // Cancel any pending score toast - "LAS COSAS" is what matters
                        if (pendingScoreToastTimeoutsRef.current[playerIdx]) {
                            clearTimeout(pendingScoreToastTimeoutsRef.current[playerIdx]);
                            pendingScoreToastTimeoutsRef.current[playerIdx] = null;
                        }

                        // Close selector first
                        setWildcardSelector({ ...wildcardSelector, open: false });

                        // Wait a moment for the binding animation to complete
                        await new Promise(r => setTimeout(r, GAME_TIMINGS.lasCosas.afterBindWildcard));

                        // Reveal any covered card before showing Las Cosas
                        const coverState = cardCoverStateRef.current[playerIdx] || { coveredCardIndex: null };
                        if (coverState && coverState.coveredCardIndex !== null) {
                            debugLog(`🎴 [Baciga] Revealing covered card before Las Cosas (after binding) for player ${playerIdx}`);
                            await dealManagerRendererRef.current.revealCoveredCard(playerIdx, coverState.coveredCardIndex);
                            const newState = { ...coverState, coveredCardIndex: null };
                            setCardCoverState(prev => ({ ...prev, [playerIdx]: newState }));
                            cardCoverStateRef.current[playerIdx] = newState;
                            await new Promise(r => setTimeout(r, GAME_TIMINGS.lasCosas.afterRevealCard));
                        }

                        const playerTeamColor = teamColors.team1.hex; // Player 0 is always team1
                        safeShowPlayerToast('¡LAS COSAS!', playerIdx, { type: 'success', teamColor: playerTeamColor, duration: GAME_TIMINGS.lasCosas.toastDuration });

                        // Wait a moment before revealing all cards
                        await new Promise(r => setTimeout(r, GAME_TIMINGS.lasCosas.beforeRevealAll));

                        // Reveal all player cards for visual verification
                        await dealManagerRendererRef.current.revealAllPlayerCards();

                        // Award points (this may trigger win)
                        awardPoints(playerIdx, 18, 'Las Cosas');

                        // Wait for players to see the result before ending
                        await new Promise(r => setTimeout(r, GAME_TIMINGS.lasCosas.afterVictory));

                        endBacigaHand();
                        return;
                    } else {
                        // User made Las Cosas but busted?
                        debugLog('🃏 [Baciga] Las Cosas detected but score > 31:', score.points);
                        // Fall through to handle bust below
                    }
                }

                // 2. Check for Bust (score > 31)
                if (score.points > 31) {
                    // Cancel any pending score toast - "PASADO" is what matters
                    if (pendingScoreToastTimeoutsRef.current[playerIdx]) {
                        clearTimeout(pendingScoreToastTimeoutsRef.current[playerIdx]);
                        pendingScoreToastTimeoutsRef.current[playerIdx] = null;
                    }

                    // Reveal covered card if exists before showing PASADO toast
                    const coverState = cardCoverStateRef.current[playerIdx] || { coveredCardIndex: null };
                    if (coverState && coverState.coveredCardIndex !== null) {
                        await dealManagerRendererRef.current.revealCoveredCard(playerIdx, coverState.coveredCardIndex);
                        const newState = { ...coverState, coveredCardIndex: null };
                        setCardCoverState(prev => ({ ...prev, [playerIdx]: newState }));
                        cardCoverStateRef.current[playerIdx] = newState;
                    }

                    const teamId = (playerIdx === 0 || playerIdx === 2) ? 1 : 2;
                    const playerTeamColor = teamId === 1 ? teamColors.team1.hex : teamColors.team2.hex;
                    safeShowPlayerToast('¡PASADO!', playerIdx, { type: 'error', teamColor: playerTeamColor, duration: 2000 });
                    const marker = teamId === 1 ? team1Marker : team2Marker;
                    marker.subtractPoints(1);

                    // Close selector
                    setWildcardSelector({ ...wildcardSelector, open: false });

                    if (playerIdx === currentDealerIndexRef.current) {
                        end31Phase();
                    } else {
                        // Human is player 0. If they bust, turn passes to next player.
                        // We need to trigger next turn processing.
                        processTurn31(1);
                    }
                    return;
                }
            }
        }

        setWildcardSelector({ ...wildcardSelector, open: false });
    };

    // Table background color (green felt)
    const tableColor = 0x0d5c1e;

    // CSS styles for rotation fallback when Screen Orientation API fails
    const rotationWrapperStyle = needsRotation ? {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vh',
        height: '100vw',
        transform: 'rotate(90deg)',
        transformOrigin: 'top left',
        marginLeft: '100vw',
        overflow: 'hidden',
    } : {};

    return (
        <Box sx={rotationWrapperStyle}>
            {/* Main game interface */}
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                height: needsRotation ? '100vw' : '100dvh',
                width: needsRotation ? '100vh' : '100%',
                overflow: 'hidden',
                padding: 0,
                margin: 0,
                alignItems: 'stretch',
                position: 'relative'
            }}>
                    <Box ref={headerRef} sx={{ width: '100%', flexShrink: 0 }}>
                        <Header
                            title="Baciga"
                            onBack={() => navigate('/')}
                            dealer={startingDealer && players[currentDealerIndex]}
                            player={players[currentTurn]}
                            globalScore={globalScore}
                            players={players}
                            showSettings={false}
                            onInGameSettings={() => setInGameSettingsOpen(true)}
                        />
                    </Box>

                    {/* Game Start Sequence - shown instead of game table */}
                    {showGameStart && (
                        <GameStartSequence
                            players={players}
                            initialDealer={startingDealer}
                            onComplete={handleGameStartComplete}
                        />
                    )}

                    {/* Main Game Table - only shown when not in start sequence */}
                    {!showGameStart && (
                        <Box
                            sx={{
                                overflow: 'hidden',
                                padding: 0,
                                margin: 0,
                                minHeight: 0,
                                // Use 100vw for height when rotated via CSS
                                height: headerHeight 
                                    ? (needsRotation ? `calc(100vw - ${headerHeight}px)` : `calc(100dvh - ${headerHeight}px)`)
                                    : 'auto',
                            }}
                        >
                            <GameTable onMount={setupGame} backgroundColor={tableColor} />
                        </Box>
                    )}

                    {/* Torito decision buttons - Shown before dealing when human is dealer */}
                    {showToritoButton && waitingForToritoDecision && (
                        <Box sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2,
                            backgroundColor: 'rgba(0,0,0,0.9)',
                            padding: 3,
                            borderRadius: 2,
                            border: '2px solid #FFD700',
                            boxShadow: '0 8px 16px rgba(255, 215, 0, 0.5)',
                            zIndex: 1200
                        }}>
                            <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 'bold', textAlign: 'center' }}>
                                ¿Repartir con TORITO?
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#fff', textAlign: 'center', mb: 1 }}>
                                Una carta se repartirá bocarriba para todos
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button
                                    variant="contained"
                                    sx={{
                                        background: '#FF4444',
                                        '&:hover': { background: '#CC0000' },
                                        fontSize: '1rem',
                                        fontWeight: 'bold',
                                        padding: '12px 30px'
                                    }}
                                    onClick={() => handleToritoDecision(false)}
                                >
                                    No
                                </Button>
                                <Button
                                    variant="contained"
                                    sx={{
                                        background: '#FFD700',
                                        color: '#000',
                                        '&:hover': { background: '#FFA500' },
                                        fontSize: '1rem',
                                        fontWeight: 'bold',
                                        padding: '12px 30px'
                                    }}
                                    onClick={() => handleToritoDecision(true)}
                                >
                                    Sí, con Torito
                                </Button>
                            </Box>
                        </Box>
                    )}

                    {/* Wildcard binding button - Fixed position bottom right */}
                    {showVoteButtons && phase === 'voting' && hasUnboundWildcard && (
                        <Button
                            variant="contained"
                            sx={{
                                position: 'absolute',
                                bottom: isSelectingCards ? 200 : 20,
                                right: 20,
                                color: '#000 !important',
                                background: '#FFD700 !important',
                                backgroundColor: '#FFD700 !important',
                                border: '2px solid #FFD700',
                                '&:hover': {
                                    background: '#FFA500 !important',
                                    backgroundColor: '#FFA500 !important',
                                    border: '2px solid #FF8C00',
                                    transform: 'scale(1.05)'
                                },
                                fontSize: '0.9rem',
                                fontWeight: 'bold',
                                padding: '10px 20px',
                                boxShadow: '0 4px 8px rgba(255, 215, 0, 0.5)',
                                zIndex: 1000,
                                transition: 'bottom 0.3s ease'
                            }}
                            onClick={handleOpenWildcardSelector}
                        >
                            🎴 Amarrar Comodín
                        </Button>
                    )}

                    {showVoteButtons && phase === 'voting' && !isSelectingCards && !waitingForHumanCounter && (
                        <Box sx={{ position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={handleStartCardSelection}
                                    disabled={Object.values(votes).some(v => v === 'cartas')}
                                    title={Object.values(votes).some(v => v === 'cartas') ? 'Ya se cantó Cartas - solo puedes cantar Báciga o Bacigote' : 'Pasar y descartar cartas'}
                                >
                                    Paso
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => handleVote(0, 'baciga')}
                                    disabled={!playerScore?.isBaciga}
                                    title={playerScore?.isBaciga ? 'Cantar Báciga' : `Necesitas 9 o menos (tienes ${playerScore?.points})`}
                                >
                                    Báciga
                                </Button>
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={() => handleVote(0, 'bacigote')}
                                    disabled={!playerScore?.isBacigote}
                                    title={playerScore?.isBacigote ? 'Cantar Bacigote' : 'Necesitas 3 cartas iguales'}
                                >
                                    Bacigote
                                </Button>
                                {handNumber > 1 && (
                                    <Button
                                        variant="contained"
                                        color="warning"
                                        onClick={() => handleVote(0, 'cartas')}
                                        disabled={Object.values(votes).some(v => v === 'cartas')}
                                        title={Object.values(votes).some(v => v === 'cartas') ? 'Ya se cantó Cartas' : 'Cantar Cartas (ir a Las 31)'}
                                    >
                                        Cartas
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    )}

                    {/* Counter-declaration buttons - shown when AI declares and human can counter */}
                    {showVoteButtons && phase === 'voting' && waitingForHumanCounter && (
                        <Box sx={{
                            position: 'absolute',
                            bottom: 20,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2,
                            backgroundColor: 'rgba(0,0,0,0.8)',
                            padding: 2,
                            borderRadius: 2
                        }}>
                            <Typography sx={{ color: 'yellow', fontWeight: 'bold', fontSize: '1rem' }}>
                                ¡Un rival ha cantado! ¿Quieres declarar?
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={() => handleHumanCounterDeclaration('paso')}
                                >
                                    No declarar
                                </Button>
                                {playerScore?.isBaciga && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => handleHumanCounterDeclaration('baciga')}
                                    >
                                        ¡Báciga! ({playerScore?.minPoints} pts)
                                    </Button>
                                )}
                                {playerScore?.isBacigote && (
                                    <Button
                                        variant="contained"
                                        color="error"
                                        onClick={() => handleHumanCounterDeclaration('bacigote')}
                                    >
                                        ¡Bacigote!
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    )}

                    {showVoteButtons && phase === 'voting' && isSelectingCards && (
                        <Box sx={{
                            position: 'absolute',
                            bottom: 20,
                            right: 20,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2,
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            padding: 2,
                            borderRadius: 2
                        }}>
                            <Typography sx={{ color: 'white', fontSize: '0.9rem' }}>
                                Selecciona al menos 1 carta a descartar:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                {[0, 1, 2].map(idx => {
                                    const playerCards = dealManagerRef.current?.getPlayerCards(0) || [];
                                    const card = playerCards[idx];
                                    const getCardDisplayName = (card) => {
                                        if (!card) return `Carta ${idx + 1}`;
                                        const valueNames = {
                                            1: 'As',
                                            2: '2',
                                            3: '3',
                                            4: '4',
                                            5: '5',
                                            6: '6',
                                            7: '7',
                                            10: 'Sota',
                                            11: 'Caballo',
                                            12: 'Rey'
                                        };
                                        const suitNames = {
                                            'oros': 'Oros',
                                            'copas': 'Copas',
                                            'espadas': 'Espadas',
                                            'bastos': 'Bastos'
                                        };
                                        const valueName = valueNames[card.value] || card.value;
                                        const suitName = suitNames[card.suit] || card.suit;
                                        return `${valueName} de ${suitName}`;
                                    };
                                    return (
                                        <Button
                                            key={idx}
                                            variant={selectedCardIndices.includes(idx) ? "contained" : "outlined"}
                                            color={selectedCardIndices.includes(idx) ? "primary" : "inherit"}
                                            onClick={() => handleToggleCardSelection(idx)}
                                            sx={{
                                                minWidth: 50,
                                                color: selectedCardIndices.includes(idx) ? 'white' : 'white',
                                                borderColor: 'white',
                                                fontSize: '0.85rem',
                                                textTransform: 'none'
                                            }}
                                        >
                                            {getCardDisplayName(card)}
                                        </Button>
                                    );
                                })}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={handleConfirmPass}
                                    disabled={selectedCardIndices.length === 0}
                                >
                                    Confirmar Paso ({selectedCardIndices.length} cartas)
                                </Button>
                                <Button variant="outlined" color="inherit" sx={{ color: 'white', borderColor: 'white' }} onClick={handleCancelSelection}>
                                    Cancelar
                                </Button>
                            </Box>
                        </Box>
                    )}

                    {/* Card hide selection UI - shown at start of Las 31 turn for human player */}
                    {isSelectingCardToHide && phase === 'playing31' && (
                        <Box sx={{
                            position: 'absolute',
                            bottom: 20,
                            right: 20,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2,
                            backgroundColor: 'rgba(0,0,0,0.85)',
                            padding: 3,
                            borderRadius: 2,
                            border: '2px solid #FFD700',
                            zIndex: 1001
                        }}>
                            <Typography sx={{ color: '#FFD700', fontWeight: 'bold', fontSize: '1rem', textAlign: 'center' }}>
                                ¿Quieres ocultar una carta?
                            </Typography>
                            <Typography sx={{ color: 'white', fontSize: '0.85rem', textAlign: 'center', mb: 1 }}>
                                Puedes mantener UNA carta oculta a los rivales
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                                {[0, 1, 2].map(idx => {
                                    const playerCards = dealManagerRef.current?.getPlayerCards(0) || [];
                                    const card = playerCards[idx];
                                    const getCardDisplayName = (card) => {
                                        if (!card) return `Carta ${idx + 1}`;
                                        const valueNames = {
                                            1: 'As', 2: '2', 3: '3', 4: '4', 5: '5', 6: '6', 7: '7',
                                            10: 'Sota', 11: 'Caballo', 12: 'Rey'
                                        };
                                        const suitNames = {
                                            'oros': 'Oros', 'copas': 'Copas', 'espadas': 'Espadas', 'bastos': 'Bastos'
                                        };
                                        const valueName = valueNames[card.value] || card.value;
                                        const suitName = suitNames[card.suit] || card.suit;
                                        return `${valueName} de ${suitName}`;
                                    };
                                    return (
                                        <Button
                                            key={idx}
                                            variant="outlined"
                                            onClick={() => handleSelectCardToHide(idx)}
                                            sx={{
                                                minWidth: 100,
                                                color: 'white',
                                                borderColor: '#FFD700',
                                                fontSize: '0.8rem',
                                                textTransform: 'none',
                                                '&:hover': {
                                                    borderColor: '#FFA500',
                                                    backgroundColor: 'rgba(255, 215, 0, 0.2)'
                                                }
                                            }}
                                        >
                                            Ocultar: {getCardDisplayName(card)}
                                        </Button>
                                    );
                                })}
                            </Box>
                            <Button
                                variant="contained"
                                color="success"
                                onClick={() => handleSelectCardToHide(-1)}
                                sx={{ mt: 1, fontSize: '0.9rem' }}
                            >
                                Mostrar todas mis cartas
                            </Button>
                        </Box>
                    )}

                    {/* Wildcard binding button - Fixed position bottom right (Las 31 phase) */}
                    {showVoteButtons && phase === 'playing31' && hasUnboundWildcard && !isSelectingCardToHide && (
                        <Button
                            variant="contained"
                            sx={{
                                position: 'absolute',
                                bottom: currentTurn === 0 ? 220 : 20,
                                right: 20,
                                color: '#000 !important',
                                background: '#FFD700 !important',
                                backgroundColor: '#FFD700 !important',
                                border: '2px solid #FFD700',
                                '&:hover': {
                                    background: '#FFA500 !important',
                                    backgroundColor: '#FFA500 !important',
                                    border: '2px solid #FF8C00',
                                    transform: 'scale(1.05)'
                                },
                                fontSize: '0.9rem',
                                fontWeight: 'bold',
                                padding: '10px 20px',
                                boxShadow: '0 4px 8px rgba(255, 215, 0, 0.5)',
                                zIndex: 1000,
                                transition: 'bottom 0.3s ease'
                            }}
                            onClick={handleOpenWildcardSelector}
                        >
                            🎴 Amarrar Comodín
                        </Button>
                    )}

                    {showVoteButtons && phase === 'playing31' && !isSelectingCardToHide && (
                        <Box sx={{
                            position: 'absolute',
                            bottom: 20,
                            right: 20,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1.5,
                            backgroundColor: 'rgba(0,0,0,0.8)',
                            padding: 2,
                            borderRadius: 2
                        }}>

                            {/* Reveal covered card button - only show if player has a covered card */}
                            {currentTurn === 0 && cardCoverState[0]?.coveredCardIndex !== null && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Typography sx={{ color: 'yellow', fontSize: '0.85rem', fontWeight: 'bold' }}>
                                        Tienes una carta oculta a los demás
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        color="warning"
                                        onClick={handleRevealCoveredCard}
                                        sx={{ fontSize: '0.85rem' }}
                                    >
                                        Revelar Carta (Levantar)
                                    </Button>
                                    <Typography sx={{ color: 'white', fontSize: '0.75rem', fontStyle: 'italic' }}>
                                        (Si revelas, las siguientes también serán visibles)
                                    </Typography>
                                </Box>
                            )}

                            {/* Main action buttons */}
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button variant="contained" color="success" onClick={() => handleAction31(0, 'carta')}>Carta!</Button>
                                <Button variant="contained" color="error" onClick={() => handleAction31(0, 'planto')}>Me Planto</Button>
                            </Box>
                        </Box>
                    )}
                </Box>

            {/* Wildcard Selector Dialog */}
            <WildcardSelector
                open={wildcardSelector.open}
                onClose={() => {
                    // If there's a pending action (pendingCounterDeclaration or onComplete callback),
                    // don't allow cancel - the user must select a value to continue the game
                    if (pendingCounterDeclaration || wildcardSelector.onComplete) {
                        safeShowToast(`${TOAST_ICONS.WARNING} Debes seleccionar un valor para el comodín`, { type: 'warning', duration: 2000 });
                        return;
                    }
                    // Normal close when there's no pending action
                    setWildcardSelector({ ...wildcardSelector, open: false });
                }}
                onSelect={handleWildcardSelect}
                card={wildcardSelector.card}
                currentValue={wildcardSelector.currentValue}
                playerCards={dealManagerRef.current?.getPlayerCards(0) || []}
            />

            {/* Score History Modal */}
            <ScoreHistoryModal
                open={historyModalOpen}
                onClose={() => setHistoryModalOpen(false)}
                history={selectedTeamHistory}
                teamId={selectedTeamId}
            />

            {/* Game Winner Modal */}
            <GameWinnerModal
                open={showWinnerModal}
                winnerTeamId={winnerTeamId}
                players={players}
                gamePoints={gamePoints}
                onHome={async () => {
                    await gameStateManager.clearSavedGame();
                    navigate('/');
                }}
            />

            {/* Round Winner Modal - for hand wins, not match wins */}
            <RoundWinnerModal
                open={showRoundWinnerModal}
                winnerTeamId={roundWinnerTeamId}
                gameName="Báciga"
                nextGameName={pendingNextGame?.nextGameName}
                players={players}
                teamColors={{ team1: teamColors.team1.main, team2: teamColors.team2.main }}
                onContinue={handleRoundWinnerContinue}
            />

            {/* In-Game Settings Modal */}
            <GameSettingsInGameModal
                open={inGameSettingsOpen}
                onClose={() => setInGameSettingsOpen(false)}
                isPaused={isPaused}
                onPauseToggle={handlePauseToggle}
                gameType="baciga"
                getGameState={getGameStateForSave}
                currentPhase={phase}
            />

            {/* Shuffling Modal - shown when dealer changes */}
            <ShufflingModal
                open={showShufflingModal}
                playerName={shufflingPlayerName}
                playerAvatar={shufflingPlayerAvatar}
            />

            {/* Phase 31 Summary Modal - shown before transitioning to Las 31 */}
            <Phase31SummaryModal
                open={showPhase31SummaryModal}
                summaryData={phase31SummaryData}
                players={players}
                onContinue={handlePhase31Continue}
            />

            {/* Achievement unlock toast */}
            <Snackbar
                open={!!achievementToast}
                autoHideDuration={4000}
                onClose={() => setAchievementToast(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    icon={false}
                    onClose={() => setAchievementToast(null)}
                    sx={{
                        background: 'linear-gradient(135deg, #1a1a2e, #0f3460)',
                        color: 'white',
                        border: '1px solid rgba(255,215,0,0.5)',
                        boxShadow: '0 0 20px rgba(255,215,0,0.3)',
                        borderRadius: 2,
                    }}
                >
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography fontSize="1.5rem">{achievementToast?.icon}</Typography>
                        <Box>
                            <Typography fontWeight="bold" fontSize="0.85rem">{i18n.t('achievements.unlocked_toast')}</Typography>
                            <Typography fontSize="0.8rem" sx={{ color: '#FFD700' }}>{achievementToast ? i18n.t(achievementToast.nameKey) : ''}</Typography>
                        </Box>
                    </Box>
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Baciga;
